import { createSelector, createSlice } from "@reduxjs/toolkit";
import { Buffer } from "buffer";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import axios from "../../axios";
import {
    buildApiV2Point5Url,
    buildApiV2Point5UrlWithQuery,
    buildApiV3Url,
    buildApiV3UrlWithQuery,
    convertFieldToString,
    fixSearchStringForPostgres,
    getFormattedDate,
    removeSpaces,
    toBase64,
    transformExemptionData,
} from "../../shared/Utils";
import { fieldsValidation, fieldValidationTypeRequired } from "../../shared/validation/validator";

import getConfig from "../../config";
import {
    BackgroundJobState,
    CERTIFICATE_FILTER_DEFAULTS,
    CRUDOperation,
    documentTypeNames,
    pendingCertificateColumnPreference,
    PendingStatuses,
    resyncApiCallToastMessage,
} from "../../shared/constants";
import { parseGetAllObjResponse, parseSuccessResponse } from "../../shared/responseUtils";
import { mapLocalJurisdictionsToCertificateJurisdictionCodes } from "../../features/search/Filters/mapLocalJurisdictionsToCertificateJurisdictionCodes";
import { deduplicateRequest } from "../../shared/services/apiDeduplicationService";
import { fetchAllPreferences } from "../../shared/services/preferencesService";
import { getStoredItem } from "../../shared/Utils.js";
import {
    fetchCustomerExemptionsDetails,
    setIsRequestFromCustomersCertificateGrid,
} from "../customerCertificateSlice";
import { setIsExportSearchDialogOpen } from "../exportSearchSlice";
import {
    createValidationData,
    handleInvalidData,
    processValidationResults,
} from "./autoValidationHelpers";
import certificateFiltersData from "./certificateFiltersData";
import fetchCertificatesSearchAPI from "./fetchCertificatesSearchAPI";
import fetchTaxContentFormsAsync from "./fetchTaxContentFormsAsync";
import fetchVendorCertificatesSearchAPI from "./fetchVendorCertificatesSearchAPI";
import getCertificateColumnList from "./getCertificateColumnList";
import getPendingCertificateColumnList from "./getPendingCertificateColumnList";

// Config is retrieved lazily inside functions to support runtime configuration

// Module-level promises to track in-flight requests and prevent duplicate concurrent API calls
let invalidReasonsPromise = null;
let certificateLabelsPromise = null;
let certificateAttachmentLabelsPromise = null;
let certificateStatusesPromise = null;
let certificateCustomFieldsPromise = null;
let skipIdpApply = false;

export const initialState = {
    isloading: false,
    isLoadingCountries: false,
    countries: [],
    defaultCountryId: null,
    isLoadingAssignment: false,
    limitedCountries: [],
    isUploadingFile: false,
    isAutoValidatingFile: false,
    downloadableFile: false,
    isLoadingExposureZones: false,
    exposureZones: [],
    sutOutgoingExposureZones: [],
    isLoadingDataEntrySets: false,
    dataEntrySets: [],
    regionIds: [],
    matrixZones: [],
    isLoadingExemptionReasons: false,
    isLoadingOutgoingExemptionReasons: false,
    exemptionReasons: [],
    outgoingExemptionReasons: [],
    isLoadingCoverLetter: false,
    standardCoverLetter: [],
    isLoadingExemptionMatrix: false,
    exemptionMatrix: [],
    customerCert: [],
    exemptionMatrixValidation: [],
    isLoadingCustomerCert: false,
    isPostingCert: false,
    isCertPresent: true,
    isDeletingCert: false,
    isDeletingCertFile: false,
    noDataType: "",
    noSearch: true,
    certificateFormState: {
        exposureZone: "",
        exposureCountry: "",
        taxCode: "",
        expirationDate: "",
        isSingle: false,
        valid: false,
        poNumberValue: "",
        poNumberId: 0,
    },
    formState: {
        smartSearchInput: "",
        nameOrCode: "",
        nameOrCodeWhileLoading: "",
        smartSearchError: "",
    },
    certificateFiltersState: CERTIFICATE_FILTER_DEFAULTS,
    certificateStatuses: [],
    isLoadingCertificateStatuses: false,
    page: {
        data: [],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 20,
            totalRecords: 0,
        },
    },
    pendingFormState: {
        nameOrCodeOrFileOrId: "",
    },
    pendingCertificateFiltersState: {
        exposureZone: [],
        certificateCreatedFrom: "",
        certificateCreatedTo: "",
        assignedTo: null,
        escalated: null,
        isCertificateImage: 0,
        source: 0,
        exemptReasons: [],
        savedSearch: "",
        certificateIds: "",
        certificateIdError: "",
        priority: [],
        bucket: [],
        customerCertificateCustomFields: {},
        localJurisdictions: [],
    },
    isLoadingValidationData: false,
    validationData: { fileType: "", data: "", fileName: "" },
    fvsValidationData: { Key: "", FileName: "", ValidationId: "" },
    hasFileUnderValidation: false,
    hasCertificateFile: false,
    sortColumn: ["id", false],
    isLoadingInvalidReasons: false,
    invalidReasons: [],
    labels: [],
    isLoadingLabels: false,
    isLoadingCertificateCustomFields: false,
    isLoadingCustomFields: false,
    customFields: [],
    certificateCustomFields: [],
    isAttachingFile: false,
    isAttachingComment: false,
    isLoadingSupplementalFiles: false,
    isLoadingComments: false,
    supplementalFiles: [],
    pendingSupplementalFiles: [],
    modifiedSupplementalFiles: {},
    isLoadingSelectedColumns: false,
    isLoadingPendingCertificateSelectedColumns: false,
    pendingCertificateSelectedColumns: [
        { id: "created-date", label: "Date", hidden: false },
        { id: "customer-name", label: "Customer name", hidden: false },
        { id: "customer-code", label: "Customer code", hidden: false },
        { id: "exposure-zone-name", label: "Region", hidden: false },
        { id: "orig-file-name", label: "File name", hidden: false },
        { id: "preview", label: "Preview", hidden: false },
        { id: "source", label: "Source", hidden: false },
        { id: "assigned-to-user-name", label: "Assigned to", hidden: true },
        { id: "escalation-date", label: "Escalation", hidden: true },
        { id: "uploaded-by", label: "Uploaded by", hidden: false },
    ],
    selectedColumns: [
        { id: "name", label: "Customer Name", hidden: false },
        { id: "customerCode", label: "Customer code", hidden: false },
        { id: "exposureZoneName", label: "Region", hidden: false },
        { id: "taxCodeName", label: "Exemption reason", hidden: false },
        { id: "createdDate", label: "Added", hidden: false },
        { id: "signedDate", label: "Effective", hidden: false },
        { id: "expirationDate", label: "Expiration", hidden: false },
        { id: "certificateStatus", label: "Status", hidden: false },
        { id: "invalidReasons", label: "Invalid reasons", hidden: true },
        { id: "isSingle", label: "Single Use", hidden: true },
        { id: "purchaseOrderNumber", label: "PO number", hidden: true },
        { id: "taxNumber", label: "Tax ID", hidden: true },
        { id: "certificateFilename", label: "File name", hidden: true },
        { id: "formType", label: "Form type", hidden: true },
        { id: "msValidated", label: "MS Validated", hidden: true },
    ],
    vendorColumns: [
        { id: "name", label: "Vendor name", hidden: false },
        { id: "customerCode", label: "Vendor code", hidden: false },
        { id: "exposureZoneName", label: "Region", hidden: false },
        { id: "taxCodeName", label: "Exemption reason", hidden: false },
        { id: "createdDate", label: "Added", hidden: false },
        { id: "signedDate", label: "Effective", hidden: false },
        { id: "expirationDate", label: "Expiration", hidden: false },
        { id: "certificateStatus", label: "Status", hidden: false },
        { id: "isSingle", label: "Single Use", hidden: true },
        { id: "purchaseOrderNumber", label: "PO number", hidden: true },
        { id: "certificateFilename", label: "File name", hidden: true },
        { id: "msValidated", label: "MS Validated", hidden: true },
    ],
    comments: [],
    isDeletingComment: false,
    autoValidationData: {
        issues: [],
        predictions: {},
        status: "",
        isMulti: false,
        isSinglePurchase: false,
        taskType: "",
        isInValidTaxFormat: false,
        isExpiredDate: false,
    },
    idpCertResponse: "",
    redirectURL: "",
    isMulti: false,
    isRegionValid: true,
    regionMessage: "",
    isTaxcodeMap: false,
    isLoadingCertificateExport: false,
    multiCertChildList: [],
    isUploadDialogOpen: false,
    isUploadDialogEllipsesMenuOpen: false,
    isPauseCertificateDialogOpen: false,
    isResumeCertificateDialogOpen: false,
    isLoadingMulti: false,
    isSplitCertificateDialogOpen: false,
    passedFVS: true,
    isScanningFVS: false,
    isCertificateUpdated: false,
    isSplittingCertificates: false,
    isCertificateGridEllipsesMenu: false,
    isBulkDownloadCertificateDialogOpen: false,
    isInvalidReasonDialogOpen: false,
    escalationLog: null,
    isLoadingEscalationLog: false,
    isLoadingCertificateColumnConfig: false,
    customLabels: [],
    certificateLabels: [],
    isLoadingCertificateLabels: false,
    certificateAttachmentLabels: [],
    isLoadingCertificateAttachmentLabels: false,
    certificateExistingCustomFieldValues: {},
    certificateAddedCustomFieldValues: {},
    isLoadingTaxContentForms: false,
    taxContentForms: {},
    taxContentForm: "",
    templateTag: "",
    isContentFormDialogOpen: false,
    isTaxTypeDialogOpen: false,
    isLoadingCommunicationTaxTypes: false,
    certificateCommunicationTaxTypes: [],
    mostCommonForm: "",
    isCertificateCountAPICompleted: 0,
    isLoadingCertificateCount: false,
    validationStateCount: 0,
    certificateSources: [],
    isLoadingCertificateSources: false,
    certificateReviewers: [],
    isLoadingCertificateReviewers: false,
    isCompanyGroupSelected: false,
    isSmartSearchFiltersLoading: false,
    smartSearchFilters: {},
    isReviewNextEnabled: false,
    isReviewNextDialogOpen: true,
    isMagnifierToggled: true,
    isReviewComplete: false,
    certificatesForReview: [],
    hasFilterChips: false,
};

export const certificateSlice = createSlice({
    name: "certificate",
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.isloading = action.payload;
        },
        setLoadingCountries: (state, action) => {
            state.isLoadingCountries = action.payload;
        },
        setIsLoadingCertificateAssignment: (state, action) => {
            state.isLoadingAssignment = action.payload;
        },
        setCountries: (state, action) => {
            state.countries = action.payload;
        },
        setDefaultCountryId: (state, action) => {
            state.defaultCountryId = action.payload;
        },
        setLimitedCountries: (state, action) => {
            state.limitedCountries = action.payload;
        },
        setUploadingFile: (state, action) => {
            state.isUploadingFile = action.payload;
        },
        setIsAutoValidatingFile: (state, action) => {
            state.isAutoValidatingFile = action.payload;
        },
        setDownloadableFile: (state, action) => {
            state.downloadableFile = action.payload;
        },
        setLoadingExposureZones: (state, action) => {
            state.isLoadingExposureZones = action.payload;
        },
        setExposureZones: (state, action) => {
            state.exposureZones = action.payload;
        },
        setSUTOutgoingExposureZones: (state, action) => {
            state.sutOutgoingExposureZones = action.payload;
        },
        setIsLoadingDataEntrySets: (state, action) => {
            state.isLoadingDataEntrySets = action.payload;
        },
        setDataEntrySets: (state, action) => {
            state.dataEntrySets = action.payload;
        },
        setRegionIds: (state, action) => {
            state.regionIds = action.payload;
        },
        setLoadingExemptionReasons: (state, action) => {
            state.isLoadingExemptionReasons = action.payload;
        },
        setLoadingOutgoingExemptionReasons: (state, action) => {
            state.isLoadingOutgoingExemptionReasons = action.payload;
        },
        setExemptionReasons: (state, action) => {
            state.exemptionReasons = action.payload;
        },
        setOutgoingExemptionReasons: (state, action) => {
            state.outgoingExemptionReasons = action.payload;
        },
        setStandardCoverLetter: (state, action) => {
            state.standardCoverLetter = action.payload;
        },
        setLoadingCoverLetter: (state, action) => {
            state.isLoadingCoverLetter = action.payload;
        },
        setLoadingExemptionMatrix: (state, action) => {
            state.isLoadingExemptionMatrix = action.payload;
        },
        setExemptionMatrix: (state, action) => {
            state.exemptionMatrix = action.payload;
        },
        setExemptionMatrixValidation: (state, action) => {
            state.exemptionMatrixValidation = action.payload;
        },
        setMatrixZones: (state, action) => {
            state.matrixZones = action.payload;
        },
        setCertificate: (state, action) => {
            state.customerCert = action.payload;
        },
        setLoadingCustomerCert: (state, action) => {
            state.isLoadingCustomerCert = action.payload;
        },
        setPostingCert: (state, action) => {
            state.isPostingCert = action.payload;
        },
        setCertPresent: (state, action) => {
            state.isCertPresent = action.payload;
        },
        setDeletingCert: (state, action) => {
            state.isDeletingCert = action.payload;
        },
        setDeletingCertFile: (state, action) => {
            state.isDeletingCertFile = action.payload;
        },
        setTaxCode: (state, action) => {
            state.taxCode = action.payload;
        },
        setCertificateStatuses: (state, action) => {
            state.certificateStatuses = action.payload;
        },
        setExposureCountry: (state, action) => {
            state.exposureCountry = action.payload;
        },
        setCertificateFormState: (state, action) => {
            state.certificateFormState = action.payload;
        },
        clearCertificateFiltersState: state => {
            state.certificateFiltersState = initialState.certificateFiltersState;
        },
        clearPendingFiltersState: state => {
            state.pendingCertificateFiltersState = initialState.pendingCertificateFiltersState;
        },
        clearPendingFormState: state => {
            state.pendingFormState = initialState.pendingFormState;
        },
        clearCertPresent: state => {
            state.isCertPresent = initialState.isCertPresent;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPageToNull: (state, action) => {
            state.page = {
                data: [],
                paginate: {
                    pageNumber: 0,
                    startIndex: -1,
                    endIndex: 0,
                    rowsPerPage: action?.payload || initialState.page.rowsPerPage,
                    totalRecords: 0,
                },
            };
        },
        setNoDataType: (state, action) => {
            state.noDataType = action.payload;
        },
        setNoSearch: (state, action) => {
            state.noSearch = action.payload;
        },
        setIsLoadingValidationData: (state, action) => {
            state.isLoadingValidationData = action.payload;
        },
        setValidationData: (state, action) => {
            state.validationData = action.payload;
        },
        setFvsValidationData: (state, action) => {
            state.fvsValidationData = action.payload;
        },
        clearValidationData: state => {
            state.validationData = initialState.validationData;
        },
        clearFvsValidationData: state => {
            state.fvsValidationData = initialState.fvsValidationData;
        },
        setHasFileUnderValidation: (state, action) => {
            state.hasFileUnderValidation = action.payload;
        },
        setHasCertificateFile: (state, action) => {
            state.hasCertificateFile = action.payload;
        },
        setPendingState: (state, action) => {
            state.pendingFormState = action.payload;
        },
        setSortColumn: (state, action) => {
            state.sortColumn = action.payload;
        },
        setLabels: (state, action) => {
            state.labels = action.payload;
        },
        setLoadingLabels: (state, action) => {
            state.isLoadingLabels = action.payload;
        },
        setLoadingCertificateStatuses: (state, action) => {
            state.isLoadingCertificateStatuses = action.payload;
        },
        setLoadingInvalidReasons: (state, action) => {
            state.isLoadingInvalidReasons = action.payload;
        },
        setInvalidReasons: (state, action) => {
            state.invalidReasons = action.payload;
        },
        setLoadingCustomFields: (state, action) => {
            state.isLoadingCustomFields = action.payload;
        },
        setLoadingCertificateCustomFields: (state, action) => {
            state.isLoadingCertificateCustomFields = action.payload;
        },
        setCustomFields: (state, action) => {
            state.customFields = action.payload;
        },
        setCertificateCustomFields: (state, action) => {
            state.certificateCustomFields = action.payload;
        },
        setIsAttachingFile: (state, action) => {
            state.isAttachingFile = action.payload;
        },
        setLoadingSupplementalFiles: (state, action) => {
            state.isLoadingSupplementalFiles = action.payload;
        },
        setSupplementalFiles: (state, action) => {
            state.supplementalFiles = action.payload;
        },
        addPendingSupplementalFile: (state, action) => {
            const payload = action.payload;
            // Temporary client-side ID for unsaved attachments; server assigns real ID on POST /validation
            const id = `pending-${Date.now()}-${state.pendingSupplementalFiles.length}`;
            state.pendingSupplementalFiles.push({
                id,
                Key: payload.Key,
                FileName: payload.FileName,
                ValidationId: payload.ValidationId,
                documentLabelId: payload.documentLabelId ?? null,
                certificateLabelName: payload.certificateLabelName ?? null,
                entry: payload.FileName,
                isPending: true,
                created: payload.created ?? new Date(),
                accountName: payload.accountName ?? "",
            });
        },
        updatePendingSupplementalFile: (state, action) => {
            const { id, documentLabelId, fileDetails, certificateLabelName } = action.payload;
            const idx = state.pendingSupplementalFiles.findIndex(f => f.id === id);
            if (idx === -1) return;
            const item = state.pendingSupplementalFiles[idx];
            if (documentLabelId !== undefined) item.documentLabelId = documentLabelId;
            if (certificateLabelName !== undefined)
                item.certificateLabelName = certificateLabelName;
            if (fileDetails) {
                item.Key = fileDetails.key ?? fileDetails.Key;
                item.FileName = fileDetails.name ?? fileDetails.FileName;
                item.ValidationId = fileDetails.validationId ?? fileDetails.ValidationId;
                item.entry = fileDetails.name ?? fileDetails.FileName;
            }
        },
        removePendingSupplementalFile: (state, action) => {
            state.pendingSupplementalFiles = state.pendingSupplementalFiles.filter(
                f => f.id !== action.payload
            );
        },
        clearPendingSupplementalFiles: state => {
            state.pendingSupplementalFiles = [];
        },
        setModifiedSupplementalFile: (state, action) => {
            const { id, documentLabelId, fileDetails, certificateLabelName } = action.payload;
            state.modifiedSupplementalFiles[id] = {
                ...(documentLabelId !== undefined && { documentLabelId }),
                ...(fileDetails && { fileDetails }),
                ...(certificateLabelName !== undefined && { certificateLabelName }),
            };
        },
        clearModifiedSupplementalFiles: state => {
            state.modifiedSupplementalFiles = {};
        },
        setIsLoadingSelectedColumns: (state, action) => {
            state.isLoadingSelectedColumns = action.payload;
        },
        setSelectedColumns: (state, action) => {
            state.selectedColumns = action.payload;
        },
        setIsLoadingPendingCertificateSelectedColumns: (state, action) => {
            state.isLoadingPendingCertificateSelectedColumns = action.payload;
        },
        setPendingCertificateSelectedColumns: (state, action) => {
            state.pendingCertificateSelectedColumns = action.payload;
        },

        setVendorColumns: (state, action) => {
            state.vendorColumns = action.payload;
        },
        setCertificateFiltersState: (state, action) => {
            const key = Object.keys(action.payload)[0];
            state.certificateFiltersState[key] = action.payload[key];

            if (
                (key === "certificateRegions" || key === "exemptReasons") &&
                state.certificateFiltersState.localJurisdictions?.length > 0
            ) {
                state.certificateFiltersState.localJurisdictions = [];
            }
        },
        setPendingFiltersState: (state, action) => {
            const key = Object.keys(action.payload)[0];
            state.pendingCertificateFiltersState[key] = action.payload[key];

            if (
                (key === "exposureZone" || key === "exemptReasons") &&
                state.pendingCertificateFiltersState.localJurisdictions?.length > 0
            ) {
                state.pendingCertificateFiltersState.localJurisdictions = [];
            }
        },
        setFormState: (state, action) => {
            state.formState = action.payload;
        },
        clearFormState: state => {
            state.formState = initialState.formState;
        },
        setIsAttachingComment: (state, action) => {
            state.isAttachingComment = action.payload;
        },

        setLoadingComments: (state, action) => {
            state.isLoadingComments = action.payload;
        },
        setComments: (state, action) => {
            state.comments = action.payload;
        },
        setDeletingComment: (state, action) => {
            state.isDeletingComment = action.payload;
        },
        setAutoValidationData: (state, action) => {
            state.autoValidationData = action.payload;
        },
        setIdpCertResponse: (state, action) => {
            state.idpCertResponse = action.payload;
        },
        setRedirectURL: (state, action) => {
            state.redirectURL = action.payload;
        },
        setSavedSearch: (state, action) => {
            state.certificateFiltersState.savedSearch = action.payload;
        },
        setSavedSearchState: (state, action) => {
            state.certificateFiltersState = {
                ...initialState.certificateFiltersState,
                ...action.payload,
            };
        },
        setPendingSavedSearchState: (state, action) => {
            state.pendingCertificateFiltersState = {
                ...initialState.pendingCertificateFiltersState,
                ...action.payload,
            };
        },
        setPendingSavedSearch: (state, action) => {
            state.pendingCertificateFiltersState.savedSearch = action.payload;
        },
        setIsMulti: (state, action) => {
            state.isMulti = action.payload;
        },
        setIsRegionValid: (state, action) => {
            state.isRegionValid = action.payload;
        },
        setIsRegionMessage: (state, action) => {
            state.regionMessage = action.payload;
        },
        setIsTaxcodeMap: (state, action) => {
            state.isTaxcodeMap = action.payload;
        },
        setIsLoadingCertificateExport: (state, action) => {
            state.isLoadingCertificateExport = action.payload;
        },
        setMultiCertChildList: (state, action) => {
            state.multiCertChildList = action.payload;
        },
        setIsUploadDialogOpen: (state, action) => {
            state.isUploadDialogOpen = action.payload;
        },
        setIsUploadDialogEllipsesMenuOpen: (state, action) => {
            state.isUploadDialogEllipsesMenuOpen = action.payload;
        },
        setIsPauseCertificateDialogOpen: (state, action) => {
            state.isPauseCertificateDialogOpen = action.payload;
        },
        setIsResumeCertificateDialogOpen: (state, action) => {
            state.isResumeCertificateDialogOpen = action.payload;
        },
        setIsLoadingMulti: (state, action) => {
            state.isLoadingMulti = action.payload;
        },
        setIsSplitCertificateDialogOpen: (state, action) => {
            state.isSplitCertificateDialogOpen = action.payload;
        },
        setIsScanningFVS: (state, action) => {
            state.isScanningFVS = action.payload;
        },
        setPassedFVS: (state, action) => {
            state.passedFVS = action.payload;
        },
        setIsCertificateUpdated: (state, action) => {
            state.isCertificateUpdated = action.payload;
        },
        setIsSplittingCertificates: (state, action) => {
            state.isSplittingCertificates = action.payload;
        },
        setIsCertificateGridEllipsesMenu: (state, action) => {
            state.isCertificateGridEllipsesMenu = action.payload;
        },
        setIsBulkDownloadCertificateDialogOpen: (state, action) => {
            state.isBulkDownloadCertificateDialogOpen = action.payload;
        },
        setIsInvalidReasonDialogOpen: (state, action) => {
            state.isInvalidReasonDialogOpen = action.payload;
        },
        setEscalationLog: (state, action) => {
            state.escalationLog = action.payload;
        },
        setIsLoadingEscalationLog: (state, action) => {
            state.isLoadingEscalationLog = action.payload;
        },
        setIsLoadingCertificateColumnConfig: (state, action) => {
            state.isLoadingCertificateColumnConfig = action.payload;
        },
        setCustomLabels: (state, action) => {
            state.customLabels = action.payload;
        },
        setCertificateLabels: (state, action) => {
            state.certificateLabels = action.payload;
        },
        setLoadingCertificateLabels: (state, action) => {
            state.isLoadingCertificateLabels = action.payload;
        },
        setCertificateAttachmentLabels: (state, action) => {
            state.certificateAttachmentLabels = action.payload;
        },
        setLoadingCertificateAttachmentLabels: (state, action) => {
            state.isLoadingCertificateAttachmentLabels = action.payload;
        },
        setCertificateExistingCustomFieldValues: (state, action) => {
            if (Object.keys(action.payload)[0]) {
                Object.keys(action.payload).forEach(i => {
                    state.certificateExistingCustomFieldValues[i] = action.payload[i];
                });
            }
        },
        setCertificateAddedCustomFieldValues: (state, action) => {
            if (!action.payload || typeof action.payload !== "object") {
                return state;
            }
            const key = Object.keys(action.payload)[0];
            if (action.payload[key] === null) {
                delete state.certificateAddedCustomFieldValues[key];
                return state;
            }

            state.certificateAddedCustomFieldValues[key] = action.payload[key];
            return state;
        },
        clearCertificateAddedCustomFieldsValues: state => {
            state.certificateAddedCustomFieldValues = {};
        },
        clearCertificateExistingCustomFieldsValues: state => {
            state.certificateExistingCustomFieldValues = {};
        },
        setIsLoadingTaxContentForms: (state, action) => {
            state.isLoadingTaxContentForms = action.payload;
        },
        setTaxContentForms: (state, action) => {
            state.taxContentForms = action.payload;
        },
        setTaxContentForm: (state, action) => {
            state.taxContentForm = action.payload;
        },
        setTaxTemplateTag: (state, action) => {
            state.templateTag = action.payload;
        },
        setIsContentFormDialogOpen: (state, action) => {
            state.isContentFormDialogOpen = action.payload;
        },
        setIsTaxTypeDialogOpen: (state, action) => {
            state.isTaxTypeDialogOpen = action.payload;
        },
        setIsLoadingCommunicationTaxTypes: (state, action) => {
            state.isLoadingCommunicationTaxTypes = action.payload;
        },
        setCertificateCommunicationTaxTypes: (state, action) => {
            state.certificateCommunicationTaxTypes = action.payload;
        },
        setExistingTaxTypesReset: (state, action) => {
            state.existingTaxTypesReset = action.payload;
        },
        setMostCommonForm: (state, action) => {
            state.mostCommonForm = action.payload;
        },
        setCertificateCountAPICompleted: (state, action) => {
            state.isCertificateCountAPICompleted = action.payload;
        },
        incrementCertificateCountAPICompleted: state => {
            state.isCertificateCountAPICompleted += 1;
        },
        decrementCertificateCountAPICompleted: state => {
            state.isCertificateCountAPICompleted -= 1;
        },
        setIsLoadingCertificateCount: (state, action) => {
            state.isLoadingCertificateCount = action.payload;
        },
        incrementValidationStateCount: state => {
            state.validationStateCount += 1;
        },
        setValidationStateCount: (state, action) => {
            state.validationStateCount = action.payload;
        },
        setLoadingCertificateSources: (state, action) => {
            state.isLoadingCertificateSources = action.payload;
        },
        setCertificateSources: (state, action) => {
            state.certificateSources = action.payload;
        },
        setLoadingCertificateReviewers: (state, action) => {
            state.isLoadingCertificateReviewers = action.payload;
        },
        setCertificateReviewers: (state, action) => {
            state.certificateReviewers = action.payload;
        },
        setIsCompanyGroupSelected: (state, action) => {
            state.isCompanyGroupSelected = action.payload;
        },
        setSmartSearchFiltersLoading: (state, action) => {
            state.isSmartSearchFiltersLoading = action.payload;
        },
        setSmartSearchFilters: (state, action) => {
            state.smartSearchFilters = action.payload;
        },
        setIsReviewNextEnabled: (state, action) => {
            state.isReviewNextEnabled = action.payload;
        },
        setIsReviewNextDialogOpen: (state, action) => {
            state.isReviewNextDialogOpen = action.payload;
        },
        setIsMagnifierToggled: (state, action) => {
            state.isMagnifierToggled = action.payload;
        },
        setCertificatesForReview: (state, action) => {
            state.certificatesForReview = action.payload;
        },
        setIsReviewComplete: (state, action) => {
            state.isReviewComplete = action.payload;
        },
        clearValidationDetailState: state => {
            state.customerCert = initialState.customerCert;
            state.isCertPresent = initialState.isCertPresent;
            state.fvsValidationData = initialState.fvsValidationData;
            state.autoValidationData = initialState.autoValidationData;
            state.idpCertResponse = initialState.idpCertResponse;
            state.isAutoValidatingFile = initialState.isAutoValidatingFile;
            state.hasCertificateFile = initialState.hasCertificateFile;
            state.escalationLog = initialState.escalationLog;
            state.isRegionValid = initialState.isRegionValid;
            state.regionMessage = initialState.regionMessage;
            state.isTaxcodeMap = initialState.isTaxcodeMap;
            state.isScanningFVS = initialState.isScanningFVS;
            state.passedFVS = initialState.passedFVS;
            state.regionIds = initialState.regionIds;
            state.comments = initialState.comments;
            state.supplementalFiles = initialState.supplementalFiles;
            state.pendingSupplementalFiles = initialState.pendingSupplementalFiles;
            state.modifiedSupplementalFiles = initialState.modifiedSupplementalFiles;
            state.isLoadingTaxContentForms = initialState.isLoadingTaxContentForms;
            state.taxContentForm = initialState.taxContentForm;
            state.templateTag = initialState.templateTag;
            state.isContentFormDialogOpen = initialState.isContentFormDialogOpen;
            state.isTaxTypeDialogOpen = initialState.isTaxTypeDialogOpen;
            state.validationStateCount = initialState.validationStateCount;
            state.certificateCommunicationTaxTypes = initialState.certificateCommunicationTaxTypes;
            state.validationData = initialState.validationData;
            // Clear custom field values to prevent contamination between certificates
            state.certificateExistingCustomFieldValues =
                initialState.certificateExistingCustomFieldValues;
            state.certificateAddedCustomFieldValues =
                initialState.certificateAddedCustomFieldValues;
            // Clear certificate labels to prevent contamination between certificates
            state.certificateLabels = initialState.certificateLabels;
            state.certificateAttachmentLabels = initialState.certificateAttachmentLabels;
        },
        setHasFilterChips: (state, action) => {
            state.hasFilterChips = action.payload;
        },
    },
});

export const {
    clearValidationDetailState,
    setLoading,
    setLoadingCountries,
    setIsLoadingCertificateAssignment,
    setCountries,
    setDefaultCountryId,
    setLimitedCountries,
    setUploadingFile,
    setIsAutoValidatingFile,
    setDownloadableFile,
    setLoadingExposureZones,
    setExposureZones,
    setIsLoadingDataEntrySets,
    setDataEntrySets,
    setRegionIds,
    setLoadingExemptionReasons,
    setLoadingOutgoingExemptionReasons,
    setExemptionReasons,
    setOutgoingExemptionReasons,
    setLoadingCoverLetter,
    setStandardCoverLetter,
    setSUTOutgoingExposureZones,
    setLoadingExemptionMatrix,
    setLoadingOutgoingExemptionMatrix,
    setExemptionMatrix,
    setExemptionMatrixValidation,
    setMatrixZones,
    setLoadingCustomerCert,
    setPostingCert,
    clearPendingFormState,
    setCertPresent,
    clearCertPresent,
    setDeletingCert,
    setDeletingCertFile,
    setCertificate,
    setExposureCountry,
    setCertificateFormState,
    setLoadingLabels,
    setLabels,
    setPage,
    setPageToNull,
    setNoDataType,
    setNoSearch,
    setIsLoadingValidationData,
    setValidationData,
    setHasCertificateFile,
    setHasFileUnderValidation,
    clearValidationData,
    clearFvsValidationData,
    setPendingState,
    setSortColumn,
    setCertificateStatuses,
    setLoadingCertificateStatuses,
    setInvalidReasons,
    setLoadingInvalidReasons,
    setCustomFields,
    setCertificateCustomFields,
    setLoadingCustomFields,
    setLoadingCertificateCustomFields,
    setIsAttachingFile,
    setSupplementalFiles,
    addPendingSupplementalFile,
    updatePendingSupplementalFile,
    removePendingSupplementalFile,
    clearPendingSupplementalFiles,
    setModifiedSupplementalFile,
    clearModifiedSupplementalFiles,
    setLoadingSupplementalFiles,
    setIsLoadingSelectedColumns,
    setSelectedColumns,
    setIsLoadingPendingCertificateSelectedColumns,
    setPendingCertificateSelectedColumns,
    setVendorColumns,
    setCertificateFiltersState,
    clearCertificateFiltersState,
    setFormState,
    clearFormState,
    setIsAttachingComment,
    setComments,
    setLoadingComments,
    setDeletingComment,
    setPendingFiltersState,
    clearPendingFiltersState,
    setAutoValidationData,
    setIdpCertResponse,
    setRedirectURL,
    setSavedSearch,
    setSavedSearchState,
    setPendingSavedSearch,
    setPendingSavedSearchState,
    setIsMulti,
    setIsRegionValid,
    setIsRegionMessage,
    setIsTaxcodeMap,
    setIsLoadingCertificateExport,
    setMultiCertChildList,
    setIsUploadDialogOpen,
    setIsUploadDialogEllipsesMenuOpen,
    setIsPauseCertificateDialogOpen,
    setIsResumeCertificateDialogOpen,
    setIsLoadingMulti,
    setIsSplitCertificateDialogOpen,
    setIsScanningFVS,
    setPassedFVS,
    setIsCertificateUpdated,
    setIsSplittingCertificates,
    setIsCertificateGridEllipsesMenu,
    setIsBulkDownloadCertificateDialogOpen,
    setIsInvalidReasonDialogOpen,
    setEscalationLog,
    setIsLoadingEscalationLog,
    setFvsValidationData,
    setIsLoadingCertificateColumnConfig,
    setCustomLabels,
    setCertificateLabels,
    setLoadingCertificateLabels,
    setCertificateAttachmentLabels,
    setLoadingCertificateAttachmentLabels,
    setCertificateExistingCustomFieldValues,
    setCertificateAddedCustomFieldValues,
    clearCertificateAddedCustomFieldsValues,
    clearCertificateExistingCustomFieldsValues,
    setIsLoadingTaxContentForms,
    setTaxContentForms,
    setTaxContentForm,
    setTaxTemplateTag,
    setIsContentFormDialogOpen,
    setIsTaxTypeDialogOpen,
    setIsLoadingCommunicationTaxTypes,
    setCertificateCommunicationTaxTypes,
    setExistingTaxTypesReset,
    setMostCommonForm,
    setCertificateCountAPICompleted,
    incrementCertificateCountAPICompleted,
    decrementCertificateCountAPICompleted,
    setIsLoadingCertificateCount,
    incrementValidationStateCount,
    setValidationStateCount,
    setLoadingCertificateSources,
    setCertificateSources,
    setLoadingCertificateReviewers,
    setCertificateReviewers,
    setIsCompanyGroupSelected,
    setSmartSearchFiltersLoading,
    setSmartSearchFilters,
    setIsReviewNextEnabled,
    setIsReviewNextDialogOpen,
    setIsMagnifierToggled,
    setCertificatesForReview,
    setIsReviewComplete,
    setHasFilterChips,
} = certificateSlice.actions;

export const selectSmartSearchFilters = state => {
    return state.certificate.smartSearchFilters;
};

export const selectCertificateExistingCustomFieldValues = state => {
    return state.certificate.certificateExistingCustomFieldValues;
};

export const selectCertificateAddedCustomFieldValues = state => {
    return state.certificate.certificateAddedCustomFieldValues;
};

export const selectIsInvalidReasonDialogOpen = state => {
    return state.certificate.isInvalidReasonDialogOpen;
};

export const selectIsBulkDownloadCertificateDialogOpen = state => {
    return state.certificate.isBulkDownloadCertificateDialogOpen;
};

export const selectMultiCertChildList = state => {
    return state.certificate.multiCertChildList;
};

export const selectIsLoadingCertificateExport = state => {
    return state.certificate.isLoadingCertificateExport;
};

export const selectLoading = state => {
    return state.certificate.isloading;
};

export const selectIsLoadingCountries = state => {
    return state.certificate.isLoadingCountries;
};

export const selectIsLoadingAssignment = state => {
    return state.certificate.isLoadingAssignment;
};

export const selectIsUploadingFile = state => {
    return state.certificate.isUploadingFile;
};

export const selectIsAutoValidatingFile = state => {
    return state.certificate.isAutoValidatingFile;
};

export const selectIsLoadingExposureZones = state => {
    return state.certificate.isLoadingExposureZones;
};

export const selectIsLoadingExemptionReasons = state => {
    return state.certificate.isLoadingExemptionReasons;
};

export const selectIsLoadingExemptionMatrix = state => {
    return state.certificate.isLoadingExemptionMatrix;
};

export const selectIsLoadingCoverLetter = state => {
    return state.certificate.isLoadingCoverLetter;
};

export const selectIsScanningFVS = state => {
    return state.certificate.isScanningFVS;
};

export const selectPassedFVS = state => {
    return state.certificate.passedFVS;
};

export const selectIsCompanyGroupSelected = state => {
    return state.certificate.isCompanyGroupSelected;
};

export const selectCountries = createSelector([state => state.certificate.countries], countries => {
    if (countries?.length === 0) {
        return getStoredItem("countries") || [];
    }
    return countries;
});

export const selectDefaultCountryId = createSelector(
    [state => state.certificate.defaultCountryId],
    defaultCountryId => getStoredItem("defaultCountryId") || defaultCountryId
);

export const selectLimitedCountries = state => {
    if (state.certificate.limitedCountries?.length === 0) {
        return getStoredItem("limitedCountries") || [];
    }
    return state.certificate.limitedCountries;
};

export const selectExposureZones = createSelector(
    state => state.certificate,
    certificate => {
        if (certificate.exposureZones?.length === 0) {
            return getStoredItem("incomingZones") || [];
        }
        return certificate.exposureZones;
    }
);

export const selectSUTOutgoingExposureZones = createSelector(
    state => state.certificate,
    certificate => {
        if (certificate.sutOutgoingExposureZones?.length === 0) {
            return getStoredItem("outgoingZones") || [];
        }
        return certificate.sutOutgoingExposureZones;
    }
);

export const selectIsLoadingDataEntrySets = state => {
    return state.certificate.isLoadingDataEntrySets;
};

export const selectDataEntrySets = state => {
    return getStoredItem("dataentrysets") || state.certificate.dataEntrySets;
};

export const selectRegionIds = createSelector(
    state => state.certificate,
    certificate => certificate.regionIds
);

export const selectMatrixZones = state => {
    return state.certificate.matrixZones;
};

export const selectExemptionReasons = createSelector(
    [state => state.certificate.exemptionReasons],
    exemptionReasons => {
        if (exemptionReasons?.length === 0) {
            return getStoredItem("exemptReasons") || [];
        }
        return exemptionReasons;
    }
);

export const selectOutgoingExemptionReasons = createSelector(
    [state => state.certificate.outgoingExemptionReasons],
    outgoingExemptionReasons => {
        if (outgoingExemptionReasons?.length === 0) {
            return getStoredItem("vemExemptReasons") || [];
        }
        return outgoingExemptionReasons;
    }
);

export const selectStandardCoverLetter = state => {
    return state.certificate.standardCoverLetter;
};

export const selectCertificateStatuses = createSelector(
    state => state.certificate,
    certificate => {
        if (certificate.certificateStatuses?.length === 0) {
            return getStoredItem("certificateStatuses") || [];
        }
        return certificate.certificateStatuses;
    }
);

export const selectExemptionMatrix = state => {
    return state.certificate.exemptionMatrix;
};

export const selectExemptionMatrixValidation = createSelector(
    state => state.certificate,
    certificate => certificate.exemptionMatrixValidation
);

export const selectIsLoadingCustomerCert = state => {
    return state.certificate.isLoadingCustomerCert;
};

export const selectIsPostingCert = state => {
    return state.certificate.isPostingCert;
};
export const selectIsCertPresent = state => {
    return state.certificate.isCertPresent;
};
export const selectIsDeletingCert = state => {
    return state.certificate.isDeletingCert;
};

export const selectIsDeletingCertFile = state => {
    return state.certificate.isDeletingCertFile;
};

export const selectCustomerCert = createSelector(
    state => state.certificate,
    certificate => certificate.customerCert
);

export const selectDownloadableFile = state => {
    return state.certificate.downloadableFile;
};

export const selectExposureCountry = state => {
    return state.certificate.exposureCountry;
};

export const selectTaxCode = state => {
    return state.certificate.taxCode;
};
export const selectIsLoadingCertificateStatuses = state => {
    return state.certificate.isLoadingCertificateStatuses;
};

export const getCertificateFormState = state => {
    return state.certificate.certificateFormState;
};

export const selectPage = state => {
    return state.certificate.page;
};

export const selectNoData = state => {
    return state.certificate.noDataType;
};

export const selectNoSearch = state => {
    return state.certificate.noSearch;
};
export const selectLabels = state => {
    return getStoredItem("labels") || state.certificate.labels;
};
export const selectIsLoadingLabels = state => {
    return state.certificate.isLoadingLabels;
};

export const selectIsLoadingValidationData = state => {
    return state.certificate.isLoadingValidationData;
};

export const selectValidationData = createSelector(
    state => state.certificate,
    certificate => certificate.validationData
);

export const selectFvsValidationData = state => {
    return state.certificate.fvsValidationData;
};

export const selectHasFileUnderValidation = state => {
    return state.certificate.hasFileUnderValidation;
};

export const selectHasCertificateFile = state => {
    return state.certificate.hasCertificateFile;
};
export const selectSortColumn = state => {
    return state.certificate.sortColumn;
};

export const selectIsLoadingInvalidReasons = state => {
    return state.certificate.isLoadingInvalidReasons;
};

export const selectInvalidReasons = state => {
    return getStoredItem("invalidReasons") || state.certificate.invalidReasons;
};

export const selectIsLoadingCertificateCustomFields = state => {
    return state.certificate.isLoadingCertificatecustomFields;
};

export const selectCustomFields = state => {
    return state.certificate.customFields;
};

export const selectCertificateCustomFields = state => {
    return state.certificate.certificateCustomFields;
};

export const selectIsAttachingFile = state => {
    return state.certificate.isAttachingFile;
};

export const selectIsLoadingSupplementalFiles = state => {
    return state.certificate.isLoadingSupplementalFiles;
};

export const selectSupplementalFiles = state => {
    return state.certificate.supplementalFiles;
};

export const selectPendingSupplementalFiles = state => {
    return state.certificate.pendingSupplementalFiles;
};

export const selectModifiedSupplementalFiles = state => {
    return state.certificate.modifiedSupplementalFiles;
};

/**
 * Resolves attachmentLabelIds to label names using certificateLabels.
 * @param {number[]} attachmentLabelIds - Label IDs from API
 * @param {Array<{id: number, name: string}>} certificateLabels - Available labels
 * @returns {string} Comma-separated label names
 */
const resolveAttachmentLabelNames = (attachmentLabelIds, certificateLabels) => {
    if (!Array.isArray(attachmentLabelIds) || attachmentLabelIds.length === 0) return "";
    const labels = certificateLabels || [];
    return attachmentLabelIds
        .map(id => {
            const label = labels.find(
                l => l.id === id || l.id === Number(id) || String(l.id) === String(id)
            );
            return label?.name ?? null;
        })
        .filter(Boolean)
        .join(", ");
};

/**
 * Merged list for display: server supplemental files (with local modifications applied) + pending.
 * Resolves attachmentLabelIds to label names using certificateAttachmentLabels.
 */
export const selectSupplementalFilesForDisplay = state => {
    const {
        supplementalFiles,
        pendingSupplementalFiles,
        modifiedSupplementalFiles,
        certificateAttachmentLabels,
    } = state.certificate;
    const serverList = (supplementalFiles || []).map(f => {
        const mod = modifiedSupplementalFiles?.[f.id];
        let file = f;
        if (mod) {
            file = {
                ...f,
                ...(mod.documentLabelId !== undefined && {
                    documentLabelId: mod.documentLabelId,
                    certificateLabelName: mod.certificateLabelName,
                    documentLabelName: mod.certificateLabelName,
                    labelName: mod.certificateLabelName,
                }),
                ...(mod.fileDetails && {
                    entry: mod.fileDetails.name ?? mod.fileDetails.FileName,
                    Key: mod.fileDetails.key ?? mod.fileDetails.Key,
                    FileName: mod.fileDetails.name ?? mod.fileDetails.FileName,
                    ValidationId: mod.fileDetails.validationId ?? mod.fileDetails.ValidationId,
                }),
            };
        }
        const resolvedFromIds = resolveAttachmentLabelNames(
            file.attachmentLabelIds ?? f.attachmentLabelIds,
            certificateAttachmentLabels
        );
        const displayLabel =
            resolvedFromIds ||
            file.certificateLabelName ||
            file.documentLabelName ||
            file.labelName;
        return displayLabel ? { ...file, certificateLabelName: displayLabel } : file;
    });
    return [...serverList, ...(pendingSupplementalFiles || [])];
};
export const selectIsAttachingComment = state => {
    return state.certificate.isAttachingComment;
};

export const selectIsLoadingComments = state => {
    return state.certificate.isLoadingSupplementalFiles;
};

export const selectComments = state => {
    return state.certificate.comments;
};

export const selectIsDeletingComment = state => {
    return state.certificate.isDeletingComment;
};

export const selectPendingFormState = state => {
    return state.certificate.pendingFormState;
};

export const selectCertificateFiltersState = state => {
    return state.certificate.certificateFiltersState;
};

export const selectPendingCertificateFiltersState = state => {
    return state.certificate.pendingCertificateFiltersState;
};

export const selectFormState = state => {
    return state.certificate.formState;
};

export const selectAutoValidationData = state => {
    return state.certificate.autoValidationData;
};

export const selectIdpCertResponse = state => {
    return state.certificate.idpCertResponse;
};

export const selectRedirectURL = state => {
    return state.certificate.redirectURL;
};

export const selectSavedSearchState = state => {
    return state.certificate.certificateFiltersState.savedSearch;
};

export const selectIsMulti = state => {
    return state.certificate.isMulti;
};
export const selectIsRegionValid = state => {
    return state.certificate.isRegionValid;
};
export const selectRegionMessage = state => {
    return state.certificate.regionMessage;
};
export const selectTaxcodeMap = state => {
    return state.certificate.isTaxcodeMap;
};
export const selectIsUploadDialogOpen = state => {
    return state.certificate.isUploadDialogOpen;
};
export const selectIsUploadDialogEllipsesMenuOpen = state => {
    return state.certificate.isUploadDialogEllipsesMenuOpen;
};
export const selectIsPauseCertificateDialogOpen = state => {
    return state.certificate.isPauseCertificateDialogOpen;
};
export const selectIsResumeCertificateDialogOpen = state => {
    return state.certificate.isResumeCertificateDialogOpen;
};
export const selectIsSplitCertificateDialogOpen = state => {
    return state.certificate.isSplitCertificateDialogOpen;
};
export const selectIsSplittingCertificates = state => {
    return state.certificate.isSplittingCertificates;
};
export const selectIsLoadingMulti = state => {
    return state.certificate.isLoadingMulti;
};
export const selectIsCertificateUpdated = state => {
    return state.certificate.isCertificateUpdated;
};
export const selectIsCertificateGridEllipsesMenu = state => {
    return state.certificate.isCertificateGridEllipsesMenu;
};
export const selectEscalationLog = state => {
    return state.certificate.escalationLog;
};
export const selectIsLoadingEscalationLog = state => {
    return state.certificate.isLoadingEscalationLog;
};

export const selectIsLoadingSelectedColumns = state => {
    return state.certificate.isLoadingSelectedColumns;
};

export const selectIsLoadingPendingSelectedColumns = state => {
    return state.certificate.isLoadingPendingCertificateSelectedColumns;
};

export const getSelectedColumns = state => {
    return state.certificate.selectedColumns;
};

export const selectPendingCertificateSelectedColumns = state => {
    return state.certificate.pendingCertificateSelectedColumns;
};
export const selectCustomLabels = state => {
    return state.certificate.customLabels;
};
export const selectCertificateLabels = state => {
    return state.certificate.certificateLabels;
};

export const selectIsLoadingCertificateLabels = state => {
    return state.certificate.isLoadingCertificateLabels;
};

export const selectCertificateAttachmentLabels = state => {
    return state.certificate.certificateAttachmentLabels;
};

export const selectIsLoadingCertificateAttachmentLabels = state => {
    return state.certificate.isLoadingCertificateAttachmentLabels;
};

export const selectTaxContentForms = state => {
    return state.certificate.taxContentForms;
};

export const selectIsLoadingTaxContentForms = state => {
    return state.certificate.isLoadingTaxContentForms;
};

export const selectTaxContentForm = createSelector(
    state => state.certificate,
    certificate => certificate.taxContentForm
);

export const selectTaxTemplateTag = state => {
    return state.certificate.templateTag;
};

export const selectIsContentFormDialogOpen = state => {
    return state.certificate.isContentFormDialogOpen;
};

export const selectIsTaxTypeDialogOpen = state => {
    return state.certificate.isTaxTypeDialogOpen;
};

export const selectIsLoadingCommunicationTaxTypes = state => {
    return state.certificate.isLoadingCommunicationTaxTypes;
};

export const selectCertificateCommunicationTaxTypes = state => {
    return state.certificate.certificateCommunicationTaxTypes;
};

export const selectMostCommonForm = state => {
    return state.certificate.mostCommonForm;
};

export const selectIsCertificateCountAPICompleted = state => {
    return state.certificate.isCertificateCountAPICompleted;
};

export const selectIsLoadingCertificateCount = state => {
    return state.certificate.isLoadingCertificateCount;
};

export const selectValidationStateCount = state => {
    return state.certificate.validationStateCount;
};

export const selectCertificateSources = state => {
    return state.certificate.certificateSources;
};

export const selectIsLoadingCertificateSources = state => {
    return state.certificate.isLoadingCertificateSources;
};

export const selectCertificateReviewers = state => {
    return state.certificate.certificateReviewers;
};

export const selectIsLoadingCertificateReviewers = state => {
    return state.certificate.isLoadingCertificateReviewers;
};

export const selectIsReviewNextEnabled = state => {
    return state.certificate.isReviewNextEnabled;
};

export const selectIsReviewNextDialogOpen = state => {
    return state.certificate.isReviewNextDialogOpen;
};

export const selectIsMagnifierToggled = state => {
    return state.certificate.isMagnifierToggled;
};

export const selectCertificatesForReview = state => {
    return state.certificate.certificatesForReview;
};

export const selectIsReviewComplete = state => {
    return state.certificate.isReviewComplete;
};

export const selectHasFilterChips = state => {
    return state.certificate.hasFilterChips;
};

export const upsertCertificateColumnConfig = preferenceValue => async () => {
    // to handle mismatch with pre-saved ecm preferences.
    let preferenceValueUpdated;
    try {
        preferenceValueUpdated = JSON.stringify(preferenceValue).replace(
            "certificateLabels",
            "certificatesLabels"
        );
    } catch {
        preferenceValueUpdated = "{}";
    }
    await axios.post(
        buildApiV3Url(`ecm-preferences`),
        {
            preference: "certificate-columns",
            preferenceValue: preferenceValueUpdated,
        },
        { withCredentials: true }
    );
};

export const upsertDocumentColumnConfig = preferenceValue => async () => {
    // to handle mismatch with pre-saved ecm preferences.
    let preferenceValueUpdated;
    try {
        preferenceValueUpdated = JSON.stringify(preferenceValue).replace(
            "vendorCertificateLabels",
            "vendorCertificatesLabels"
        );
    } catch {
        preferenceValueUpdated = "{}";
    }
    await axios.post(
        buildApiV3Url(`ecm-preferences`),
        {
            preference: "vendor-certificate-columns",
            preferenceValue: preferenceValueUpdated,
        },
        { withCredentials: true }
    );
};

const getPreferenceConfig = async vendorColumns => {
    const preferences = await fetchAllPreferences();

    if (!preferences?.length) return vendorColumns;

    const certificatePreference = preferences.find(
        pref => pref.preference === "vendor-certificate-columns"
    );

    if (certificatePreference?.preferenceValue) {
        try {
            return JSON.parse(certificatePreference.preferenceValue);
        } catch {
            return vendorColumns;
        }
    }
    return vendorColumns;
};

const addCertificateLabelsColumn = (config, vendorColumns) => {
    const certificateLabelColumnItem = {
        id: "vendorCertificatesLabels",
        label: "Labels",
        hidden: true,
    };

    const mergedConfig = [
        ...new Map(
            [...vendorColumns, certificateLabelColumnItem, ...config].map(item => [item.id, item])
        ).values(),
    ];

    return mergedConfig.map(column =>
        column.id === certificateLabelColumnItem.id
            ? { ...column, label: certificateLabelColumnItem.label }
            : column
    );
};

const fetchCustomFields = async dispatch => {
    const response = await axios.get(
        buildApiV3Url(
            `certificate-custom-fields?$filter=documentType.Name eq '${documentTypeNames.salesAndUseTax}' and documentType.Outgoing eq true&$top=100`
        ),
        { withCredentials: true }
    );

    if (response?.data?.value) {
        dispatch(setCustomFields(response.data.value));
        return response.data.value;
    }
    return [];
};

const processCustomFields = (config, certificateCustomFields, dispatch) => {
    const filteredConfig = config.filter(
        column =>
            !column.fieldName ||
            certificateCustomFields.some(col => col.fieldName === column.fieldName)
    );

    if (!certificateCustomFields.length) return filteredConfig;

    const formatCustomFields = certificateCustomFields.map(field => ({
        id: removeSpaces(field.fieldName),
        label: field.fieldName,
        hidden: true,
        fieldName: field.fieldName,
    }));

    const mergedConfig = [
        ...new Map(
            [...formatCustomFields.reverse(), ...filteredConfig.reverse()].map(item => [
                item.id,
                item,
            ])
        ).values(),
    ].reverse();

    const finalConfig = mergedConfig.map(column => {
        const matchingData = formatCustomFields.find(data => data.id === column.id);
        return matchingData ? { ...column, fieldName: matchingData.fieldName } : column;
    });

    dispatch(upsertDocumentColumnConfig(finalConfig));
    return finalConfig;
};

export const getDocumentColumnList =
    (isEligibleUserCustomFields, isUserEligibleToViewCertificateLabels) =>
    async (dispatch, getState) => {
        const { vendorColumns, isLoadingSelectedColumns } = getState().certificate;

        if (isLoadingSelectedColumns) return false;

        dispatch(setIsLoadingSelectedColumns(true));

        let config = await getPreferenceConfig(vendorColumns);

        if (isUserEligibleToViewCertificateLabels) {
            config = addCertificateLabelsColumn(config, vendorColumns);
        }

        if (isEligibleUserCustomFields) {
            const certificateCustomFields = await fetchCustomFields(dispatch);
            config = processCustomFields(config, certificateCustomFields, dispatch);
        }

        dispatch(setVendorColumns(config));
        dispatch(setIsLoadingSelectedColumns(false));
        return true;
    };

export const upsertPendingCertificateColumnConfig = preferenceValue => async () => {
    await axios.post(
        buildApiV3Url(`ecm-preferences`),
        {
            preference: pendingCertificateColumnPreference,
            preferenceValue: (() => {
                try {
                    return JSON.stringify(preferenceValue);
                } catch {
                    return "{}";
                }
            })(),
        },
        { withCredentials: true }
    );
};

export const getVendorColumns = state => {
    return state.certificate.vendorColumns;
};

export const getCountriesAsync = () => async (dispatch, getState) => {
    const { certificate } = getState();

    if (certificate.isLoadingCountries || certificate.countries.length > 0) {
        return false;
    }
    if (getStoredItem("countries")) {
        return false;
    }

    dispatch(setLoadingCountries(true));
    const response = await axios.get(buildApiV3Url(`countries?$top=300&$orderBy=id`), {
        withCredentials: true,
    });

    const allCountries = convertFieldToString(response.data.value, "id");

    const defaultCountry = allCountries.filter(x => x.name === "United States");
    dispatch(setDefaultCountryId(defaultCountry[0].id));
    localStorage.setItem("defaultCountryId", JSON.stringify(defaultCountry[0].id));

    dispatch(setCountries(allCountries));
    localStorage.setItem("countries", JSON.stringify(allCountries));

    dispatch(setLoadingCountries(false));

    return true;
};

export const getLimitedCountriesAsync = () => async (dispatch, getState) => {
    const { certificate } = getState();
    if (certificate.isLoadingCountries || certificate.limitedCountries.length === 2) {
        return false;
    }
    if (getStoredItem("limitedCountries")) {
        return false;
    }

    dispatch(setLoadingCountries(true));
    const response = await axios.get(
        buildApiV3Url(`countries?$filter=Name IN ('United States','Canada')&$orderBy=name desc`),
        { withCredentials: true }
    );

    localStorage.setItem("limitedCountries", JSON.stringify(response.data.value));

    dispatch(setLimitedCountries(response.data.value));
    dispatch(setLoadingCountries(false));
    return true;
};

export const getExposureZonesAsync = filterString => async (dispatch, getState) => {
    const { certificate } = getState();
    if (certificate.isLoadingExposureZones) {
        return false;
    }

    const outgoingZonesCache = getStoredItem("outgoingZones");
    if (filterString?.includes("outgoing eq true") && outgoingZonesCache?.length > 0) {
        return false;
    }
    const incomingZonesCache = getStoredItem("incomingZones");
    if (!filterString?.includes("outgoing eq true") && incomingZonesCache?.length > 0) {
        return false;
    }

    dispatch(setLoadingExposureZones(true));
    const response = await axios.get(
        buildApiV3Url(`regions/${filterString}&$top=3000`),

        { withCredentials: true }
    );

    const transformedStandardZones = convertFieldToString(response.data.value, "id");
    if (filterString?.includes("outgoing eq true")) {
        localStorage.setItem("outgoingZones", JSON.stringify(transformedStandardZones));
        dispatch(setSUTOutgoingExposureZones(transformedStandardZones));
    } else {
        localStorage.setItem("incomingZones", JSON.stringify(transformedStandardZones));
        dispatch(setExposureZones(transformedStandardZones));
    }
    dispatch(setLoadingExposureZones(false));
    return true;
};

export const getDataEntrySetsAsync = () => async (dispatch, getState) => {
    const { certificate } = getState();
    if (certificate.isLoadingDataEntrySets || getStoredItem("dataentrysets")) {
        return false;
    }

    dispatch(setIsLoadingDataEntrySets(true));
    const response = await axios.get(buildApiV3Url(`dataentrysets`), {
        withCredentials: true,
    });
    if (response?.data?.value) {
        localStorage.setItem("dataentrysets", JSON.stringify(response.data.value));
        dispatch(setDataEntrySets(response.data.value));
    } else {
        dispatch(setDataEntrySets([]));
    }
    dispatch(setIsLoadingDataEntrySets(false));
    return true;
};

export const fetchRegionIdsByDataSetIdAPI = dataEntrySetId => async dispatch => {
    dispatch(setLoadingExposureZones(true));
    const response = await axios.get(buildApiV3Url(`regions/dataentryset/${dataEntrySetId}`), {
        withCredentials: true,
    });
    if (response?.data?.value) {
        const transformedRegionIds = convertFieldToString(response.data.value, "regionId");
        dispatch(setRegionIds(transformedRegionIds));
    } else dispatch(setRegionIds([]));

    dispatch(setLoadingExposureZones(false));
};

export const getCertificateStatusesAsync = () => async dispatch => {
    // Skip if data already exists in localStorage
    if (localStorage.getItem("certificateStatuses")) {
        return false;
    }

    // Use deduplication to prevent concurrent duplicate calls
    certificateStatusesPromise = deduplicateRequest(certificateStatusesPromise, async () => {
        dispatch(setLoadingCertificateStatuses(true));
        const response = await axios.get(buildApiV3Url(`certificate-statuses`), {
            withCredentials: true,
        });

        if (response && response.data && response.data.value) {
            dispatch(setCertificateStatuses(response.data.value));
            localStorage.setItem("certificateStatuses", JSON.stringify(response.data.value));
        }
        return response;
    });

    try {
        await certificateStatusesPromise;
        return true;
    } finally {
        certificateStatusesPromise = null;
        dispatch(setLoadingCertificateStatuses(false));
    }
};

export const getExemptionReasonsAsync =
    (filterString, includeTelecom = false) =>
    async (dispatch, getState) => {
        const { certificate } = getState();
        if (certificate.isLoadingExemptionReasons || certificate.exemptionReasons.length > 0) {
            return false;
        }
        if (getStoredItem("exemptReasons")) {
            return false;
        }

        if (!includeTelecom) {
            filterString += ` AND isTelecom eq false`;
        }

        dispatch(setLoadingExemptionReasons(true));
        const apiResponse = await axios.get(
            buildApiV3Url(`exemption-reasons/${filterString}&$orderBy=name ASC&$top=100`),
            {
                withCredentials: true,
            }
        );

        if (parseSuccessResponse(CRUDOperation.ReadAll, apiResponse)?.recordFound) {
            const parseReadResponse = parseGetAllObjResponse(apiResponse);
            const transformedExemptReason = convertFieldToString(parseReadResponse.value, "id");
            const exemptionReasonsRenamed = transformExemptionData(
                transformedExemptReason,
                false,
                true
            );
            dispatch(setExemptionReasons(exemptionReasonsRenamed));
            localStorage.setItem("exemptReasons", JSON.stringify(exemptionReasonsRenamed));
        }
        dispatch(setLoadingExemptionReasons(false));
        return true;
    };

// RJB: I needed to create this due to an issue with using localStorage checks for methods that get used by components across document types. We should re-evaluate why and how we do this.
export const getOutgoingExemptionReasonsAsync = filterString => async (dispatch, getState) => {
    const { certificate } = getState();

    if (
        certificate.isLoadingOutgoingExemptionReasons ||
        certificate.outgoingExemptionReasons.length > 0
    ) {
        return false;
    }
    if (getStoredItem("vemExemptReasons")) {
        return false;
    }

    dispatch(setLoadingOutgoingExemptionReasons(true));
    const response = await axios.get(
        buildApiV3Url(`exemption-reasons/${filterString}&$orderBy=name ASC&$top=100`),
        {
            withCredentials: true,
        }
    );
    if (response && response.data && response.data.value) {
        const transformedVemExemptReasons = convertFieldToString(response.data.value, "id");
        const exemptionReasonRenamed = transformExemptionData(
            transformedVemExemptReasons,
            false,
            true
        );

        dispatch(setOutgoingExemptionReasons(exemptionReasonRenamed));
        localStorage.setItem("vemExemptReasons", JSON.stringify(exemptionReasonRenamed));
    }
    dispatch(setLoadingOutgoingExemptionReasons(false));
    return true;
};

export const getSearchedCertificates = () => (dispatch, getState) => {
    const { page } = getState().certificate;
    return page;
};

// TODO: Remove legacy logic when Add Certifiate page is deprecated
export const getExemptionMatrix =
    (filterString, showToast, allowMultipleCalls, legacy = true) =>
    async (dispatch, getState) => {
        const { certificate } = getState();
        let exposureZones = [];
        if (certificate.isLoadingExemptionMatrix && !allowMultipleCalls) {
            return;
        }

        dispatch(setLoadingExemptionMatrix(true));

        const response = await axios.get(buildApiV3Url(`exemption-matrix${filterString}`), {
            withCredentials: true,
        });
        let exemptionMatrix = [];
        if (response && response.data && response.data.value) exemptionMatrix = response.data.value;

        Object.values(exemptionMatrix).forEach(element => {
            if (legacy) {
                exposureZones.push(element.region.name);
            } else {
                exposureZones.push({ value: element.region.id, label: element.region.name });
            }
        });

        if (!exposureZones.length) {
            showToast &&
                showToast(
                    "warning",
                    "Exemption matrix does not collect for selected reason for exemption."
                );
            dispatch(setMatrixZones([]));
        } else if (legacy) {
            exposureZones.sort();
            const selectZones = exposureZones.map(element => ({
                label: element,
                value: element,
            }));
            dispatch(setMatrixZones(selectZones));
        } else {
            exposureZones = orderBy(exposureZones, ["label"], ["asc"]);
            dispatch(setMatrixZones(exposureZones));
        }
        dispatch(setExemptionMatrix(exemptionMatrix));
        dispatch(setLoadingExemptionMatrix(false));
    };

export const fetchTemplateCommunicationTaxTypesAsync = filterString => async dispatch => {
    dispatch(setIsLoadingCommunicationTaxTypes(true));
    const taxTypeSort = [];

    const response = await axios.get(
        buildApiV3Url(`template-communication-tax-types${filterString}`),
        {
            withCredentials: true,
        }
    );

    if (response && response.data) {
        Object.values(response.data.value).forEach(element => {
            taxTypeSort.push({
                id: element.communicationTaxType.id.toString(),
                codeId: element.communicationTaxType.afcCode.code,
                code: element.communicationTaxType.afcCode.name,
                rateType: element.communicationTaxType.communicationTaxTypesCategory.rateType,
                exposureZoneId: element.communicationTaxType.exposureZoneId,
            });
        });

        const byRateType = taxTypeSort.slice(0);
        byRateType.sort((a, b) => {
            const x = a.rateType.toLowerCase();
            const y = b.rateType.toLowerCase();
            if (x < y) return -1;
            return x > y ? 1 : 0;
        });

        dispatch(setCertificateCommunicationTaxTypes(byRateType));
    }

    dispatch(setIsLoadingCommunicationTaxTypes(false));
    return true;
};

export const fetchCertificateCommunicationTaxTypesAsync = certificateId => async dispatch => {
    dispatch(setIsLoadingCommunicationTaxTypes(true));
    const communicationTaxTypeIds = [];
    const communicationTaxTypes = [];

    const response = await axios.get(
        buildApiV3Url(`certificates/${certificateId}/communication-tax-types?$top=1000`),

        {
            withCredentials: true,
        }
    );

    if (response && response.data) {
        Object.values(response.data.value).forEach(element => {
            if (!communicationTaxTypeIds.includes(element.communicationTaxType.id)) {
                communicationTaxTypeIds.push(element.communicationTaxType.id);
                communicationTaxTypes.push({
                    id: element.communicationTaxType.id.toString(),
                    codeId: element.communicationTaxType.afcCodeCode,
                    code: element.communicationTaxType.afcCodeName,
                    rateType: element.communicationTaxType.rateType,
                    exposureZoneId: element.communicationTaxType.exposureZoneId,
                });
            }
        });

        const byRateType = communicationTaxTypes.slice(0);

        byRateType.sort((a, b) => {
            const x = a.rateType.toLowerCase();
            const y = b.rateType.toLowerCase();

            if (x < y) return -1;
            return x > y ? 1 : 0;
        });
        dispatch(setCertificateCommunicationTaxTypes(byRateType));
    }
    dispatch(setIsLoadingCommunicationTaxTypes(false));
    return true;
};

export const fetchExemptionMatrixReasonValidationApi =
    (exposureZoneIds, showToast, documentTypeId) => async (dispatch, getState) => {
        const { certificate } = getState();
        const exposureZones = [];
        if (certificate.isLoadingExemptionMatrix) {
            return;
        }
        dispatch(setLoadingExemptionMatrix(true));
        const response = await axios.post(
            buildApiV3Url(`exemption-matrix/exempt-reasons/validation`),
            { exposureZoneIds, documentTypeId },
            { withCredentials: true }
        );
        if (response?.data?.value) {
            const exemptionMatrix = response.data.value;
            Object.values(exemptionMatrix).forEach(element => {
                exposureZones.push({ value: element.region.id, label: element.region.name });
            });
            const exemptionMatrixUpdated = transformExemptionData(exemptionMatrix, true, false);
            dispatch(setExemptionMatrixValidation(exemptionMatrixUpdated));
        } else {
            if (!exposureZones.length) {
                showToast &&
                    showToast(
                        "warning",
                        "Exemption matrix does not collect for selected reason for exemption."
                    );
            }
            dispatch(setExemptionMatrixValidation([]));
        }

        dispatch(setLoadingExemptionMatrix(false));
    };

export const fetchCertificatesAPI =
    (filterString, include, top, skip, onLanding, paginateDetails) =>
    async (dispatch, getState) => {
        const { isloading, sortColumn } = getState().certificate;
        if (isloading) {
            return;
        }
        dispatch(setLoading(true));
        filterString = fixSearchStringForPostgres(filterString);
        const apiURI = buildApiV2Point5UrlWithQuery(
            `certificates`,
            filterString,
            include,
            top,
            skip,
            sortColumn
        );
        const response = await axios.get(`${apiURI}`, { withCredentials: true });

        if (response.data && response.data.count === "0") {
            onLanding ? dispatch(setNoDataType("onLanding")) : dispatch(setNoDataType("onSearch"));
            dispatch(setPageToNull());
        } else {
            dispatch(setNoDataType(""));
            const paginateData = paginateDetails
                ? {
                      pageNumber: paginateDetails.currentPage,
                      startIndex: paginateDetails.startIndex,
                      endIndex: paginateDetails.endIndex,
                      rowsPerPage: paginateDetails.rowsPerPage,
                      totalRecords: response.data.count,
                  }
                : {
                      pageNumber: 1,
                      startIndex: 0,
                      endIndex: 19,
                      rowsPerPage: 20,
                      totalRecords: response.data.count,
                  };
            dispatch(
                setPage({
                    data: response.data.value,
                    paginate: paginateData,
                })
            );
        }

        dispatch(setLoading(false));
    };

export const reloadCertificateStatuses = async dispatch => {
    if (localStorage.getItem("certificateStatuses")) {
        return;
    }
    await dispatch(setLoadingCertificateStatuses(false));
    await dispatch(getCertificateStatusesAsync());
};

const buildExemptionStatuses = async (certificateStatuses, dispatch) => {
    await reloadCertificateStatuses(dispatch);
    const statuses = getStoredItem("certificateStatuses") || [];

    if (!statuses?.length) return [];

    return statuses.filter(status => PendingStatuses.includes(status?.name)).map(ele => ele?.id);
};

const buildRequestBody = (
    pageSize,
    pageNumber,
    sortColumn,
    exemptionStatuses,
    pendingFormState,
    pendingCertificateFiltersState
) => {
    const { nameOrCodeOrFileOrId } = pendingFormState;

    let updatedCertificateCreatedTo = "";
    if (pendingCertificateFiltersState?.certificateCreatedTo) {
        const cloneDate = new Date(pendingCertificateFiltersState.certificateCreatedTo);
        cloneDate.setDate(cloneDate.getDate() + 1);
        updatedCertificateCreatedTo = getFormattedDate(cloneDate, true);
    }

    return {
        pageSize,
        pageNumber,
        orderBy: sortColumn[0],
        orderByDirection: sortColumn[1] ? "ASC" : "DESC",
        exemptionStatus: exemptionStatuses,
        nameOrCode: nameOrCodeOrFileOrId ? fixSearchStringForPostgres(nameOrCodeOrFileOrId) : null,
        exposureZones: pendingCertificateFiltersState.exposureZone,
        certificateCreatedFrom: pendingCertificateFiltersState.certificateCreatedFrom || null,
        certificateCreatedTo: updatedCertificateCreatedTo || null,
        assignedTo: pendingCertificateFiltersState.assignedTo || 0,
        escalated: pendingCertificateFiltersState.escalated || 0,
        avsStateFilter: BackgroundJobState.Completed,
        certificateImageExists: pendingCertificateFiltersState.isCertificateImage || 0,
        source: pendingCertificateFiltersState.source,
        exemptionReasons: pendingCertificateFiltersState.exemptReasons,
        certificateCustomFields: pendingCertificateFiltersState.customerCertificateCustomFields,
        priority:
            pendingCertificateFiltersState.priority?.length > 0
                ? pendingCertificateFiltersState.priority.map(Number)
                : null,
        bucket:
            pendingCertificateFiltersState.bucket?.length > 0
                ? pendingCertificateFiltersState.bucket.map(Number)
                : null,
        certificateIds: pendingCertificateFiltersState.certificateIds?.length
            ? pendingCertificateFiltersState.certificateIds
                  .split("\n")
                  .map(value => fixSearchStringForPostgres(value.trim()))
                  .map(Number)
            : [],
        certificateJurisdictionCodes: mapLocalJurisdictionsToCertificateJurisdictionCodes(
            pendingCertificateFiltersState.localJurisdictions
        ),
    };
};

const handleSearchResponse = (
    apiResponse,
    onLanding,
    dispatch,
    getState,
    paginateDetails,
    pageNumber,
    pageSize
) => {
    if (!apiResponse.data) {
        dispatch(setNoDataType(onLanding ? "onLanding" : "onSearch"));
        dispatch(setPageToNull());
        return;
    }

    const response = transformExemptionData(apiResponse, false, false, [
        "expectedTaxCodeName",
        "taxCodeName",
    ]);
    dispatch(setNoDataType(""));

    const { page, isCertificateCountAPICompleted } = getState().certificate;
    const pageData = { ...page.paginate };

    const paginateData = paginateDetails
        ? {
              pageNumber,
              startIndex: paginateDetails.startIndex,
              endIndex: paginateDetails.endIndex,
              rowsPerPage: paginateDetails.rowsPerPage,
              totalRecords: pageData.totalRecords,
              indeterminate: false,
          }
        : {
              pageNumber,
              startIndex: 0,
              endIndex: 19,
              rowsPerPage: pageSize,
              totalRecords: isCertificateCountAPICompleted === 0 ? pageData?.totalRecords : 20,
              indeterminate: isCertificateCountAPICompleted !== 0,
          };

    const certificateIds = response?.data?.value?.map(cert => cert.certificateId);
    dispatch(setCertificatesForReview(certificateIds));
    dispatch(setPage({ data: response?.data?.value, paginate: paginateData }));
};

const handleCountRequest = (requestBody, dispatch, getState) => {
    dispatch(setIsLoadingCertificateCount(true));
    dispatch(incrementCertificateCountAPICompleted());

    axios
        .post(buildApiV3Url(`certificates/search-count`), requestBody, { withCredentials: true })
        .then(res => {
            dispatch(decrementCertificateCountAPICompleted());
            const { page } = getState().certificate;
            const pageData = { ...page.paginate };
            pageData.totalRecords = res?.data;
            pageData.indeterminate = false;
            dispatch(setPage({ data: page.data, paginate: pageData }));
            dispatch(setIsLoadingCertificateCount(false));
        })
        .catch(() => {
            dispatch(setIsLoadingCertificateCount(false));
        });
};

export const fetchPendingCertificatesSearchAPI =
    (pageSize, paginateDetails, pageNumber = 0, onLanding = false) =>
    async (dispatch, getState) => {
        const {
            isloading,
            pendingFormState,
            pendingCertificateFiltersState,
            sortColumn,
            certificateStatuses,
        } = getState().certificate;
        const { rowsPerPage } = getState().session;

        pageSize = pageSize || rowsPerPage;

        if (isloading) return;

        dispatch(setLoading(true));

        const exemptionStatuses = await buildExemptionStatuses(certificateStatuses, dispatch);
        const requestBody = buildRequestBody(
            pageSize,
            pageNumber,
            sortColumn,
            exemptionStatuses,
            pendingFormState,
            pendingCertificateFiltersState
        );

        axios
            .post(buildApiV3Url(`certificates/search`), requestBody, { withCredentials: true })
            .then(apiResponse => {
                handleSearchResponse(
                    apiResponse,
                    onLanding,
                    dispatch,
                    getState,
                    paginateDetails,
                    pageNumber,
                    pageSize
                );
            })
            .finally(() => {
                dispatch(setLoading(false));
            });

        if (!paginateDetails) {
            handleCountRequest(requestBody, dispatch, getState);
        }
    };
export const fetchValidationCertificateByIdAPI = certificateID => async (dispatch, getState) => {
    const { isloading } = getState().certificate;
    if (isloading) {
        return;
    }
    try {
        dispatch(setLoading(true));
        const response = await axios.get(
            buildApiV3Url(`certificates/${certificateID}/validation`),
            {
                withCredentials: true,
            }
        );
        if (!parseSuccessResponse(CRUDOperation.Read, response)?.recordFound) {
            dispatch(setCertificate(null));
            dispatch(setCertPresent(false));
        } else {
            const exemptionData = response.data;
            const transformedData = transformExemptionData(exemptionData, true, false);
            if (transformedData.customers && transformedData.customers.length > 0) {
                transformedData.customers = convertFieldToString(transformedData.customers, null, [
                    "country",
                    "state",
                ]);
            }
            dispatch(setCertificate(transformedData));
            dispatch(setCertPresent(true));
            const list = response?.data?.multiCertificates?.map(region => ({
                certificateId: region.certificateId,
                exposureZone: region.exposureZoneName,
            }));
            list?.length > 0 && dispatch(setIsMulti(true));
            dispatch(setMultiCertChildList(list));
        }
    } catch (error) {
        dispatch(setCertPresent(false));
        throw error;
    } finally {
        dispatch(setLoading(false));
    }
};

const badFileNames = ["document_file_does_not_exist", "N/A", "n/a"];

export const getCertificateAsync = certificateID => async (dispatch, getState) => {
    const { certificate } = getState();
    let taxCode = "";
    let exposureZone = "";
    if (certificate.isLoadingCustomerCert) {
        return;
    }

    dispatch(setLoadingCustomerCert(true));
    const queryParam = `${certificateID}?$include=customers, poNumbers, invalidReasons`;
    const response = await axios.get(buildApiV2Point5Url(`certificates/${queryParam}`), {
        withCredentials: true,
    });

    if (badFileNames.includes(response.data.fileName)) {
        dispatch(setDownloadableFile(false));
    } else {
        dispatch(setDownloadableFile(true));
    }

    if (!badFileNames.includes(response.data.fileName) && !response.data.documentExists) {
        dispatch(setHasFileUnderValidation(true));
    }

    if (response.data.actualTaxCode) {
        taxCode = response.data.actualTaxCode.name;
    } else if (response.data.status) {
        taxCode = response.data.status.name;
    }

    if (response.data.exposureZone) {
        exposureZone = response.data.exposureZone;
    }

    // TODO: move out
    dispatch(
        setCertificateFormState({
            exposureZone: exposureZone.name,
            exposureCountry: exposureZone && exposureZone.country && exposureZone.country.name,
            taxCode,
            valid: response.data.valid,
            expirationDate: response.data.expirationDate,
            isSingle: response.data.isSingle,
            poNumberValue: response.data.poNumbers[0] && response.data.poNumbers[0].poNumber,
            poNumberId: response.data.poNumbers[0] && response.data.poNumbers[0].id,
        })
    );
    dispatch(setCertificate(response.data));
    dispatch(setLoadingCustomerCert(false));
};

export const postCertificatePONumber = (certificateID, PONumber) => async () => {
    const postData = [{ poNumber: PONumber }];
    await axios.post(buildApiV2Point5Url(`certificates/${certificateID}/po-numbers`), postData, {
        withCredentials: true,
    });
};

export const addFaxEmailAddress = (postData, showToast) => async () => {
    const response = await axios.post(buildApiV3Url(`fax-emails`), postData, {
        withCredentials: true,
    });
    if (response.status === 200) {
        if (response.data.success === true) {
            showToast("success", "Client Fax Email has been added");
        } else {
            showToast("error", response?.data?.message);
        }
    } else {
        showToast("error", response?.response?.data?.[0]?.message);
    }
};

export const deleteFaxEmailAddress = (deleteData, showToast) => async () => {
    const response = await axios.delete(buildApiV3Url(`fax-emails`), {
        data: deleteData,
        withCredentials: true,
    });
    if (response.status === 200) {
        if (response.data.success === true) {
            showToast("success", "Client Fax Email has been deleted");
        } else {
            showToast("error", response?.data?.message);
        }
    } else {
        showToast("error", response?.response?.data?.[0]?.message);
    }
};

export const putCertificatePONumber = (certificate, poNumberId, poNumberValue) => async () => {
    const putData = [{ id: poNumberId, poNumber: poNumberValue }];

    await axios.put(buildApiV2Point5Url(`certificates/${certificate.id}/po-numbers`), putData, {
        withCredentials: true,
    });
};

export const putCertificateAsync = (certificateID, certificateData) => async () => {
    await axios.put(buildApiV2Point5Url(`certificates/${certificateID}`), certificateData, {
        withCredentials: true,
    });
};

async function refreshGrid(getState, dispatch) {
    // Refresh the certificates grid (Upon upload and delete success) this will help to view the updated ellipses menu
    const { isCertificateGridEllipsesMenu, page } = getState().certificate;

    // When the request is from certificate search page
    if (isCertificateGridEllipsesMenu) {
        await dispatch(
            fetchCertificatesSearchAPI(
                false,
                page.paginate,
                page.paginate.rowsPerPage,
                page.paginate.pageNumber
            )
        );
    }

    // When the request is from the customer details - Certificate grid (Customer's certificate grid)
    if (getState().customerCertificate?.isRequestFromCustomersCertificateGrid) {
        const { paginate } = getState().customerCertificate.page;
        dispatch(
            fetchCustomerExemptionsDetails(paginate.rowsPerPage, paginate.pageNumber, paginate)
        );
    }

    dispatch(setIsRequestFromCustomersCertificateGrid(false));
    dispatch(setIsCertificateGridEllipsesMenu(false));
}

export const singleDeleteCertificatesAsync =
    (certificateID, showToast, showMultipleCertDeleteMessage, afterDeleteCallback) =>
    async (dispatch, getState) => {
        dispatch(setDeletingCert(true));
        if (certificateID) {
            const response = await axios.delete(buildApiV3Url(`certificates/${certificateID}`), {
                withCredentials: true,
            });
            const toastString = `Your ${
                showMultipleCertDeleteMessage ? "certificates" : "certificate"
            } has been deleted.`;
            if (response.status === 200) {
                showToast("success", toastString);
                refreshGrid(getState, dispatch);
            }
        }
        dispatch(setDeletingCert(false));
        if (afterDeleteCallback) afterDeleteCallback();
    };

export const putCertificateAssignmentAsync =
    (certIds, assignToMeFlag, showToast) => async (dispatch, getState) => {
        if (getState().certificate.isLoadingAssignment) return;
        dispatch(setIsLoadingCertificateAssignment(true));
        const assignmentJson = {
            assignUserId: assignToMeFlag ? getState().session.userId : null,
            assignUserName: assignToMeFlag ? getState().session.username : null,
            certificatesId: certIds,
        };
        const response = await axios.put(
            buildApiV3Url(`certificates/certificates-assignment`),
            assignmentJson,
            { withCredentials: true }
        );
        if (response?.status === 200) {
            if (certIds.length > 1) {
                if (assignToMeFlag)
                    showToast("success", `${certIds.length} certificates are now assigned to you`);
                else showToast("success", `You released ${certIds.length} certificates`);
            } else if (assignToMeFlag)
                showToast("success", "The certificate is now assigned to you.");
            else showToast("success", "You released the certificate.");
        }
        dispatch(setIsLoadingCertificateAssignment(false));
    };
export const putCertificateEscalationAsync =
    (certificateId, escalationNote) => async (dispatch, getState) => {
        if (getState().certificate.isLoading) return;
        dispatch(setIsLoadingEscalationLog(true));
        const currentDate = new Date();
        const escalationJson = {
            escalationDate: currentDate,
            escalationLog: escalationNote,
            certificateId,
        };
        const response = await axios.put(
            buildApiV3Url(`certificates/certificates-escalation`),
            escalationJson,
            { withCredentials: true }
        );
        if (response?.status === 200) {
            dispatch(setEscalationLog(response.data));
        }
        dispatch(setIsLoadingEscalationLog(false));
    };

export const putCertificateBucketAsync =
    (certificateIds, bucketId, showToast) => async (dispatch, getState) => {
        const bucketName =
            getState()?.buckets?.bucketsDetail?.find(bucket => bucket.id === Number(bucketId))
                ?.name || "";
        if (getState().certificate.isLoading) return;
        dispatch(setLoading(true));
        const bucketJson = {
            certificatesId: certificateIds,
            bucketId: bucketId,
        };

        try {
            const response = await axios.put(
                buildApiV3Url(`certificates/certificates-bucket`),
                bucketJson,
                { withCredentials: true }
            );
            if (response?.status === 200 && response?.data?.success) {
                showToast("success", `Selected certificates are now assigned to ${bucketName}`);
                dispatch(setLoading(false));
                return true;
            } else {
                showToast("error", response?.data?.message || "Failed to change bucket");
                dispatch(setLoading(false));
                return false;
            }
        } catch {
            showToast("error", "Failed to change bucket. Please try again later.");
            dispatch(setLoading(false));
            return false;
        }
    };

export const deleteCertificateFile = certificateID => async (dispatch, getState) => {
    dispatch(setDeletingCertFile(true));
    const response = await axios.delete(
        buildApiV3Url(`certificates/${certificateID}/certificate-file`),
        { withCredentials: true }
    );
    dispatch(setDeletingCertFile(false));
    if (response?.data?.success) {
        dispatch(setDownloadableFile(false));
        refreshGrid(getState, dispatch);
        return true;
    }

    dispatch(setDownloadableFile(true));
    return false;
};

export const getCertificateFile = (certificateID, showToast) => async () => {
    await axios
        .get(buildApiV3Url(`certificates/${certificateID}/certificate-file`), {
            withCredentials: true,
            responseType: "blob",
        })
        .then(response => {
            if (response.headers["content-type"].includes("application/json")) {
                showToast("error", "Failed to download certificate file. Please try again later");
                return;
            }
            const blob = new Blob([response.data], { type: response.data.type });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", certificateID);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        });
};

function triggerZipDownload(blob, contentDisposition, certificateId) {
    const filename =
        contentDisposition?.match(/filename="?([^"]+)"?/)?.[1] ||
        `Certificate-${certificateId}.zip`;
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(url);
}

async function getErrorMessageFromError(error) {
    const defaultMsg = "Failed to download certificate and attachments. Please try again later.";
    if (!error.response?.data) return defaultMsg;
    try {
        const data = error.response.data;
        const text = typeof data === "string" ? data : await data.text();
        const json = JSON.parse(text);
        return json.message || json.error || defaultMsg;
    } catch {
        return defaultMsg;
    }
}

/**
 * Downloads certificate with all attachments as a ZIP file.
 * Endpoint: GET /api/v3/companies/{companyId}/certificates/{certificateId}/certificate-with-attachments
 * @param {number} companyId - Company ID
 * @param {number} certificateId - Certificate ID
 * @param {Function} showToast - Toast function (type, message)
 */
export const getCertificateWithAttachments = (companyId, certificateId, showToast) => async () => {
    try {
        const response = await axios.get(
            buildApiV3Url(
                `companies/${companyId}/certificates/${certificateId}/certificate-with-attachments`
            ),
            {
                withCredentials: true,
                responseType: "blob",
            }
        );

        const contentType = response.headers["content-type"] || "";
        if (contentType.includes("application/zip")) {
            const blob = new Blob([response.data], { type: "application/zip" });
            triggerZipDownload(blob, response.headers["content-disposition"], certificateId);
        } else if (contentType.includes("application/json")) {
            const text = await response.data.text();
            const json = JSON.parse(text);
            const msg =
                !json.success && json.message
                    ? json.message
                    : "Certificate file for certificate was not found.";
            showToast("error", msg);
        } else {
            showToast("error", "Unexpected response format. Please try again later.");
        }
    } catch (error) {
        const message = await getErrorMessageFromError(error);
        showToast("error", message);
    }
};

export const postResyncCertificateAsync = (certificateId, showToast) => async dispatch => {
    if (!certificateId) {
        showToast("error", "Certificate ID is required");
        return;
    }

    dispatch(setLoading(true));

    const response = await axios.post(
        buildApiV3Url(`certificates/${certificateId}/queue-api-resend`),
        {},
        { withCredentials: true }
    );

    if (response.status === 200) {
        if (Object.hasOwn(response.data, "-1")) {
            showToast("error", response.data["-1"]);
        } else {
            showToast("success", resyncApiCallToastMessage);
        }
    }

    dispatch(setLoading(false));
    return response;
};

export const postCertificateAsync = certificateData => async dispatch => {
    dispatch(setPostingCert(true));
    const response = await axios.post(buildApiV2Point5Url(`certificates`), certificateData, {
        withCredentials: true,
    });

    dispatch(setCertificate(response.data));
    dispatch(setPostingCert(false));
};

export const getValidationFile = certificateID => async (dispatch, getState) => {
    if (getState().certificate.isLoadingValidationData) return;
    dispatch(setIsLoadingValidationData(true));
    const response = await axios.get(
        buildApiV3Url(`certificates/${certificateID}/certificate-file`),
        {
            responseType: "arraybuffer",
            withCredentials: true,
        }
    );
    if (response.headers["content-type"].includes("application/json")) {
        let buffer;
        try {
            buffer = JSON.parse(Buffer.from(response.data));
        } catch {
            buffer = { success: false };
        }
        dispatch(setHasCertificateFile(false));
        dispatch(
            setValidationData({
                fileType: `${response.headers["content-type"]}`,
                data: buffer,
                fileName: "",
            })
        );
        if (buffer.success) dispatch(setHasFileUnderValidation(true));
        else dispatch(setHasFileUnderValidation(false));
    } else {
        dispatch(setHasCertificateFile(true));
        dispatch(setHasFileUnderValidation(false));
        let fileName = "";
        if (response.headers["content-disposition"]?.split("=").length > 1)
            fileName = response.headers["content-disposition"]?.split("=")[1].replace(/"/g, "");
        if (response && response.data) {
            const buffer = Buffer.from(response.data, "binary").toString("base64");
            dispatch(
                setValidationData({
                    fileType: `data:${response.headers["content-type"]};base64`,
                    data: `data:${response.headers["content-type"]};base64,${buffer}`,
                    fileName,
                })
            );
        }
    }

    dispatch(setIsLoadingValidationData(false));
};

/**
 * Detects FVS upload payload (Key, FileName, ValidationId) vs FormData with file.
 * When image is uploaded via FVS, backend may queue img2pdf; we must call getValidationFile
 * to show "Formatting your file..." or the PDF once ready, and avoid using fileData.get("file").
 */
const isFvsUploadPayload = fileData =>
    fileData &&
    typeof fileData === "object" &&
    !(fileData instanceof FormData) &&
    Object.hasOwn(fileData, "Key") &&
    Object.hasOwn(fileData, "ValidationId");

/**
 * Sets validation state from an uploaded file (FormData file object).
 */
const setValidationFromFile = async (fileObj, dispatch) => {
    const base64Result = await toBase64(fileObj);
    const [fileType] = base64Result.split(",");
    const fileName = fileObj.name ? fileObj.name : "";
    dispatch(setHasCertificateFile(true));
    dispatch(setHasFileUnderValidation(false));
    dispatch(setValidationData({ fileType, data: base64Result, fileName }));
};

/**
 * Syncs validation state after upload: from file (FormData) or from API (FVS payload).
 */
const syncValidationStateAfterUpload = async (certificateID, fileData, callGetAPI, dispatch) => {
    if (!callGetAPI) return;

    const fileObj = fileData instanceof FormData ? fileData.get("file") : null;
    if (fileObj) {
        await setValidationFromFile(fileObj, dispatch);
        return;
    }
    if (isFvsUploadPayload(fileData)) {
        await dispatch(getValidationFile(certificateID));
    }
};

export const uploadFileAsync =
    (certificateID, fileData, callGetAPI, showToast) => async (dispatch, getState) => {
        dispatch(setUploadingFile(true));
        dispatch(clearValidationData());

        try {
            const res = await axios.post(
                buildApiV3Url(`certificates/${certificateID}/certificate-file`),
                fileData,
                { withCredentials: true }
            );

            dispatch(setDownloadableFile(true));
            if (showToast && res.status === 200) {
                showToast("success", "Certificate image added.");
            }

            await syncValidationStateAfterUpload(certificateID, fileData, callGetAPI, dispatch);
            await refreshGrid(getState, dispatch);
        } catch {
            dispatch(setDownloadableFile(false));
        } finally {
            dispatch(setUploadingFile(false));
        }
    };

export const replaceFileAsync =
    (certificateID, fileData, callGetAPI, showToast) => async (dispatch, getState) => {
        dispatch(setUploadingFile(true));
        dispatch(clearValidationData());
        await axios
            .put(buildApiV3Url(`certificates/${certificateID}/certificate-file`), fileData, {
                withCredentials: true,
            })
            .then(async res => {
                dispatch(setDownloadableFile(true));
                if (showToast && res.status === 200)
                    showToast("success", "Certificate image added.");
                await refreshGrid(getState, dispatch);
                callGetAPI && dispatch(getValidationFile(certificateID));
            })
            .catch(() => {
                dispatch(setDownloadableFile(false));
            })
            .then(() => {
                dispatch(setUploadingFile(false));
            });
    };

/**
 * Set auto validation results from IDP response
 * Refactored to reduce cognitive complexity
 * Helper functions extracted to autoValidationHelpers.js
 */
export const setAutoValidationResults = (
    autoValidationResults,
    type,
    dispatch,
    certificateUpdated,
    onlyParse = false
) => {
    // Validate input data
    if (
        !autoValidationResults ||
        typeof autoValidationResults !== "object" ||
        Array.isArray(autoValidationResults)
    ) {
        handleInvalidData(
            dispatch,
            type,
            onlyParse,
            setIsAutoValidatingFile,
            setAutoValidationData
        );
        return;
    }

    const { isMulti, isSinglePurchase } = autoValidationResults;

    // Process all validation results
    const { issues, predictions, hasTaxFormatError, hasExpiredDate } =
        processValidationResults(autoValidationResults);

    // Create validation data
    const validationData = createValidationData(
        issues,
        predictions,
        isMulti,
        isSinglePurchase,
        type,
        hasTaxFormatError,
        hasExpiredDate
    );

    // Return data if onlyParse, otherwise dispatch
    if (onlyParse) {
        return validationData;
    }

    dispatch(setIsAutoValidatingFile(false));
    dispatch(setAutoValidationData(validationData));
};

export const skipPendingIdpValidation = () => {
    skipIdpApply = true;
};

export const clearAutoValidation = () => async dispatch => {
    dispatch(setIsAutoValidatingFile(false));
    dispatch(
        setAutoValidationData({
            status: "",
            issues: [],
            predictions: {},
            taskType: "",
            isInValidTaxFormat: false,
            isExpiredDate: false,
        })
    );
    dispatch(setIdpCertResponse(""));
};

const extractValidationDataFromIdpResponse = response => {
    let validationData = null;

    if (response.validationResults?.ruleProcessingResult?.requiredCheck) {
        validationData = response.validationResults.ruleProcessingResult.requiredCheck;
    } else if (response.validationResults) {
        validationData = response.validationResults;
    } else if (response.requiredCheck) {
        validationData = response.requiredCheck;
    } else {
        validationData = response;
    }

    return validationData;
};

export const autoValidateFileAsync = fileData => async (dispatch, getState) => {
    if (getState().certificate.isAutoValidatingFile) return;

    skipIdpApply = false;
    dispatch(setIsAutoValidatingFile(true));

    try {
        fileData.append("source", "ECM_REVIEW_PAGE_UPLOAD");
        const response = await axios.post(buildApiV3Url(`idp/certificate-validation`), fileData, {
            withCredentials: true,
        });

        if (skipIdpApply) {
            dispatch(setIsAutoValidatingFile(false));
            return;
        }

        if (!response.data) {
            dispatch(setIsAutoValidatingFile(false));
            dispatch(
                setAutoValidationData({
                    status: "error",
                    issues: [],
                    predictions: {},
                    taskType: "",
                })
            );
            return;
        }

        const responseData = response.data;
        const { taskType: type = "" } = response.data;

        dispatch(setIdpCertResponse(JSON.stringify(responseData)));

        if (responseData.validationResults === null) {
            dispatch(setIsAutoValidatingFile(false));
            dispatch(
                setAutoValidationData({
                    status: "error",
                    issues: ["Couldn't auto-fill the details"],
                    predictions: {},
                    isMulti: false,
                    isSinglePurchase: false,
                    taskType: type,
                })
            );
            return;
        }

        const validationData = extractValidationDataFromIdpResponse(responseData);
        dispatch(setIsAutoValidatingFile(false));

        if (validationData && typeof validationData === "object") {
            setAutoValidationResults(
                validationData,
                type,
                dispatch,
                getState().certificate.isCertificateUpdated
            );
        } else {
            dispatch(
                setAutoValidationData({
                    status: "error",
                    issues: ["No validation data found in response"],
                    predictions: {},
                    isMulti: false,
                    isSinglePurchase: false,
                    taskType: type,
                })
            );
        }
    } catch (error) {
        dispatch(setIsAutoValidatingFile(false));
        if (skipIdpApply) {
            return;
        }
        dispatch(
            setAutoValidationData({
                status: "error",
                issues: [],
                predictions: {},
                taskType: "",
            })
        );
        throw error;
    }
};

/**
 * Fetches all invalid reasons with deduplication.
 * @returns {Promise<Array>} Array of invalid reasons
 */
export const getInvalidReasons = () => async (dispatch, getState) => {
    const { invalidReasons } = getState().certificate;

    // Return early if data already exists in Redux or localStorage
    if (invalidReasons.length > 0) {
        return invalidReasons;
    }
    if (getStoredItem("invalidReasons")) {
        return [];
    }

    // Use deduplication to prevent concurrent duplicate calls
    invalidReasonsPromise = deduplicateRequest(invalidReasonsPromise, async () => {
        dispatch(setLoadingInvalidReasons(true));
        const apiResponse = await axios.get(
            buildApiV3Url(`invalid-reasons?$orderBy=name ASC&$top=100`),
            {
                withCredentials: true,
            }
        );
        if (parseSuccessResponse(CRUDOperation.ReadAll, apiResponse)?.recordFound) {
            const responseResult = parseGetAllObjResponse(apiResponse);
            return responseResult.value.map(reason => ({
                ...reason,
                id: reason.id.toString(),
            }));
        }
        return [];
    });

    try {
        const result = await invalidReasonsPromise;
        dispatch(setInvalidReasons(result));
        localStorage.setItem("invalidReasons", JSON.stringify(result));
        return result;
    } finally {
        invalidReasonsPromise = null;
        dispatch(setLoadingInvalidReasons(false));
    }
};
export const getLabels = () => async (dispatch, getState) => {
    const { certificate, isLoadingLabels } = getState();
    if (isLoadingLabels || certificate.labels.length > 0) {
        return false;
    }
    if (getStoredItem("labels")) {
        return false;
    }
    dispatch(setLoadingLabels(true));
    const response = await axios.get(
        buildApiV3Url(`certificate-labels/bulkupdate-certificatelabels?$orderBy=name ASC&$top=100`),
        { withCredentials: true }
    );
    if (response?.data?.value) {
        const transformedLabels = convertFieldToString(response.data.value, "id");
        dispatch(setLabels(transformedLabels));
        localStorage.setItem("labels", JSON.stringify(transformedLabels));
    }
    dispatch(setLoadingLabels(false));
    return true;
};

export const getSupplementalFiles = certificateId => async (dispatch, getState) => {
    const { certificate } = getState();
    if (certificate.isLoadingSupplementalFiles) {
        return false;
    }
    if (Number.isNaN(certificateId) || certificateId === 0) {
        dispatch(setSupplementalFiles(certificate.supplementalFiles));
    } else {
        dispatch(setLoadingSupplementalFiles(true));
        const response = await axios.get(
            buildApiV3UrlWithQuery(
                `certificates/${certificateId}/certificate-comments?commentType=1&$top=50&$orderBy=id desc`
            ),
            { withCredentials: true }
        );
        if (response && response.data && response.data.value) {
            dispatch(setSupplementalFiles(response.data.value));
        }
        dispatch(setLoadingSupplementalFiles(false));
    }
    return true;
};

export const attachSupplementalFile = (certificateID, fileData) => async (dispatch, getState) => {
    const { certificate, session } = getState();
    if (Number.isNaN(certificateID) || certificateID === 0) {
        dispatch(
            setSupplementalFiles([
                ...certificate.supplementalFiles,
                {
                    ...fileData,
                    id: certificate.supplementalFiles.length + 1,
                    entry: fileData.FileName,
                    accountName: session.username,
                    created: new Date(),
                },
            ])
        );
    } else {
        dispatch(setIsAttachingFile(true));
        try {
            const payload = {
                commentFile: {
                    key: fileData.Key ?? fileData.key,
                    fileName: fileData.FileName ?? fileData.name,
                    validationId: fileData.ValidationId ?? fileData.validationId,
                },
                attachmentLabelIds:
                    fileData.documentLabelId != null ? [Number(fileData.documentLabelId)] : [],
            };
            const url =
                session?.activeCompany?.id != null
                    ? `companies/${session.activeCompany.id}/certificates/${certificateID}/certificate-comments/file`
                    : `certificates/${certificateID}/certificate-comments/file`;
            await axios.post(buildApiV3Url(url), payload, { withCredentials: true });
            await dispatch(getSupplementalFiles(certificateID));
        } catch (err) {
            dispatch(setIsAttachingFile(false));
            throw err;
        }
        dispatch(setIsAttachingFile(false));
    }
};

export const deleteSupplementalFile =
    (certificateID, fileId, showToast) => async (dispatch, getState) => {
        const { certificate } = getState();
        if (Number.isNaN(certificateID) || certificateID === 0) {
            dispatch(
                setSupplementalFiles(
                    [...certificate.supplementalFiles].filter(item => item.id !== fileId)
                )
            );
        } else {
            const response = await axios.delete(
                buildApiV3Url(`certificates/${certificateID}/certificate-comments/${fileId}/file`),
                { withCredentials: true }
            );
            if (response && response.status === 200) {
                showToast("success", "Attachment deleted successfully.");
            }
        }
        dispatch(getSupplementalFiles(certificateID));
    };

/**
 * Builds the payload for the single PUT to update a supplemental file.
 * API: PUT companies/{companyId}/certificates/{certificateID}/certificate-comments/{commentId}/file
 * Accepts { attachmentLabelIds?: number[], commentFile?: { key, fileName, validationId } }.
 * Replaces the previous two-call flow (label + file) with one call that updates both when provided.
 */
function buildSupplementalFileUpdatePayload(payload) {
    const { documentLabelId, fileDetails } = payload || {};
    const hasLabelChange = documentLabelId !== undefined;
    const hasFileChange = fileDetails && (fileDetails.key || fileDetails.Key);
    const result = {};
    if (hasLabelChange) {
        result.attachmentLabelIds = documentLabelId != null ? [Number(documentLabelId)] : [];
    }
    if (hasFileChange) {
        result.commentFile = {
            key: fileDetails?.key ?? fileDetails?.Key,
            fileName: fileDetails?.name ?? fileDetails?.FileName,
            validationId: fileDetails?.validationId ?? fileDetails?.ValidationId,
        };
    }
    return result;
}

export const updateSupplementalFile =
    (certificateID, commentId, payload, showToast) => async (dispatch, getState) => {
        if (Number.isNaN(certificateID) || Number.isNaN(commentId)) return;
        const companyId = getState().session?.activeCompany?.id;
        if (!companyId) return;

        const updatePayload = buildSupplementalFileUpdatePayload(payload);
        if (Object.keys(updatePayload).length === 0) {
            dispatch(getSupplementalFiles(certificateID));
            return;
        }

        const fileUrl = `companies/${companyId}/certificates/${certificateID}/certificate-comments/${commentId}/file`;
        await axios.put(buildApiV3Url(fileUrl), updatePayload, { withCredentials: true });
        showToast("success", "Supporting document updated successfully.");
        dispatch(getSupplementalFiles(certificateID));
    };

export const getCertificateCommentsAsync = certificateId => async (dispatch, getState) => {
    const { certificate } = getState();
    if (certificate.isLoadingComments) {
        return false;
    }
    if (Number.isNaN(certificateId) || certificateId === 0) {
        dispatch(setComments(certificate.comments));
    } else {
        dispatch(setLoadingComments(true));
        const response = await axios.get(
            buildApiV3Url(
                `certificates/${certificateId}/certificate-comments?commentType=0&$top=50`
            ),
            { withCredentials: true }
        );
        if (response && response.data && response.data.value)
            dispatch(setComments(response.data.value));
        else dispatch(setComments([]));
        dispatch(setLoadingComments(false));
    }
    return true;
};

export const attachCommentsAsync = (certificateID, commentText) => async (dispatch, getState) => {
    dispatch(setIsAttachingComment(true));
    if (Number.isNaN(certificateID) || certificateID === 0) {
        const { certificate, session } = getState();
        dispatch(
            setComments([
                ...certificate.comments,
                {
                    id: certificate.comments.length + 1,
                    aiUserName: session.username,
                    accountName: session.accountName,
                    entry: commentText,
                    created: new Date(),
                },
            ])
        );
    } else {
        await axios.post(
            buildApiV3Url(`certificates/${certificateID}/certificate-comments`),
            { entry: commentText },
            { withCredentials: true }
        );
        dispatch(getCertificateCommentsAsync(certificateID));
    }
    dispatch(setIsAttachingComment(false));
};

export const deleteCertificateCommentAsync =
    (certificateID, commentId) => async (dispatch, getState) => {
        const { certificate } = getState();
        if (Number.isNaN(certificateID) || certificateID === 0) {
            dispatch(setComments([...certificate.comments].filter(item => item.id !== commentId)));
        } else {
            dispatch(setDeletingComment(true));
            await axios.delete(
                buildApiV3Url(`certificates/${certificateID}/certificate-comments/${commentId}`),
                { withCredentials: true }
            );
            dispatch(setDeletingComment(false));
        }
        dispatch(getCertificateCommentsAsync(certificateID));
    };

export const postCertificateInvalidReasons = async (id, inValidReasonsData) => {
    inValidReasonsData = inValidReasonsData.map(reason => ({
        invalidReasonId: reason,
    }));

    await axios.post(buildApiV3Url(`certificates/${id}/invalid-reasons`), inValidReasonsData, {
        withCredentials: true,
    });
};

export const fetchMultiCertificateSameAs = certificateID => async (dispatch, getState) => {
    const { isLoadingMulti } = getState().certificate;
    if (isLoadingMulti) {
        return "0";
    }
    dispatch(setIsLoadingMulti(true));
    let parentId;
    const response = await axios.get(
        buildApiV3Url(`certificates/${certificateID}/multi-certificate-same-as`),
        { withCredentials: true }
    );
    if (response?.data?.childCertificateIds?.length) {
        dispatch(setIsMulti(true));
        parentId = `${response?.data?.parentCertificateId}`;
    } else {
        parentId = certificateID;
        dispatch(setIsMulti(false));
    }
    dispatch(setIsLoadingMulti(false));
    return parentId;
};

export const getTaxCodeValidation = id => async dispatch => {
    const response = await axios.get(buildApiV3Url(`certificates/taxcode-map?taxCodeId=${id}`), {
        withCredentials: true,
    });
    dispatch(setIsTaxcodeMap(true));
    if (response?.data) {
        dispatch(setIsRegionMessage(response?.data?.message));
        dispatch(setIsRegionValid(response?.data?.success));
    }
    return response?.data;
};
export const postCertificateExportApi =
    (
        selectedColumns,
        title,
        showToast,
        hideToast,
        pageSize = 20,
        pageNumber = 0,
        isVendor = false
    ) =>
    async (dispatch, getState) => {
        const { isLoadingCertificateExport } = getState().certificate;
        if (isLoadingCertificateExport) {
            return;
        }
        dispatch(setIsLoadingCertificateExport(true));
        const certificateFilterJson = certificateFiltersData(
            selectedColumns,
            getState,
            pageSize,
            pageNumber,
            isVendor
        );

        const response = await axios.post(
            buildApiV3Url(`export-certificate?title=${title}`),
            certificateFilterJson,
            { withCredentials: true }
        );
        dispatch(setIsExportSearchDialogOpen(false));
        dispatch(setIsLoadingCertificateExport(false));
        if (response.status === 200) {
            const baseURL = `https://${getConfig().hostnames.docLockerClient}`;
            window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
            showToast(
                "success",
                "We are working on your export. When it's ready, find it in",
                "export-success",
                true,
                "your downloads",
                () => {
                    hideToast("export-success");
                    window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
                },
                "The time it takes to export data depends on the size of the file and network traffic."
            );
            return;
        }
        showToast("error", `Something went wrong`);
    };

export const postPendingCertificateExportApi =
    (selectedColumns, title, showToast, hideToast, pageSize = 20, pageNumber = 0) =>
    async (dispatch, getState) => {
        const { isLoadingCertificateExport } = getState().certificate;
        if (isLoadingCertificateExport) {
            return;
        }
        dispatch(setIsLoadingCertificateExport(true));
        const certificateFilterJson = certificatePendingFiltersData(
            selectedColumns,
            getState,
            title,
            pageSize,
            pageNumber
        );
        const response = await axios.post(
            buildApiV3Url(`export-pending-certificates`),
            certificateFilterJson,
            { withCredentials: true }
        );
        dispatch(setIsExportSearchDialogOpen(false));
        dispatch(setIsLoadingCertificateExport(false));
        if (response.status === 200) {
            const baseURL = `https://${getConfig().hostnames.docLockerClient}`;
            window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
            showToast(
                "success",
                "We are working on your export. When it's ready, find it in",
                "export-success",
                true,
                "your downloads",
                () => {
                    hideToast("export-success");
                    window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
                },
                "The time it takes to export data depends on the size of the file and network traffic."
            );
            return;
        }
        showToast("error", `Something went wrong`);
    };

function certificatePendingFiltersData(
    selectedColumns,
    getState,
    title,
    pageSize = 20,
    pageNumber = 0
) {
    const { userId, accountId, role } = getState().session;
    const { pendingFormState, pendingCertificateFiltersState, sortColumn, certificateStatuses } =
        getState().certificate;
    const exemptionStatuses = [];
    let updatedCertificateCreatedTo = "";

    if (pendingCertificateFiltersState?.certificateCreatedTo) {
        const cloneDate = new Date(pendingCertificateFiltersState.certificateCreatedTo);
        cloneDate.setDate(cloneDate.getDate() + 1);
        updatedCertificateCreatedTo = getFormattedDate(cloneDate, true);
    }
    const statuses = getStoredItem("certificateStatuses") || certificateStatuses;
    if (statuses && statuses.length > 0) {
        const exemptionStatus = statuses?.filter(status => PendingStatuses.includes(status?.name));
        exemptionStatus?.forEach(ele => {
            exemptionStatuses.push(ele?.id);
        });
    }
    const { nameOrCodeOrFileOrId } = pendingFormState;

    const filterJsonObject = {
        pageSize,
        pageNumber,
        selectedColumns,
        title,
        orderBy: sortColumn[0],
        orderByDirection: sortColumn[1] ? "ASC" : "DESC",
        exemptionStatus: exemptionStatuses,
        nameOrCode: nameOrCodeOrFileOrId ? fixSearchStringForPostgres(nameOrCodeOrFileOrId) : null,
        exposureZones: pendingCertificateFiltersState.exposureZone,
        certificateCreatedFrom: pendingCertificateFiltersState.certificateCreatedFrom
            ? pendingCertificateFiltersState.certificateCreatedFrom
            : null,
        certificateCreatedTo: updatedCertificateCreatedTo || null,
        assignedTo: pendingCertificateFiltersState.assignedTo || 0,
        escalated: pendingCertificateFiltersState.escalated || 0,
        avsStateFilter: BackgroundJobState.Completed,
        certificateImageExists: pendingCertificateFiltersState.isCertificateImage || 0,
        source: pendingCertificateFiltersState.source,
        exemptionReasons: pendingCertificateFiltersState.exemptReasons,
        certificateCustomFields: pendingCertificateFiltersState.customerCertificateCustomFields,
        certificateIds: pendingCertificateFiltersState.certificateIds
            ? pendingCertificateFiltersState.certificateIds
                  .split("\n")
                  .filter(id => id.trim() !== "")
            : [],
        avalaraUserId: userId,
        avalaraUserRole: role,
        avaTaxAccountId: accountId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        priority: pendingCertificateFiltersState.priority,
        bucket: pendingCertificateFiltersState.bucket,
        certificateJurisdictionCodes: mapLocalJurisdictionsToCertificateJurisdictionCodes(
            pendingCertificateFiltersState.localJurisdictions
        ),
    };

    return filterJsonObject;
}

// This API call is used to Pause and to restore paused certificate
// To Pause --> valid : false
// To Resume --> valid : true
export const updateValidationStatusAsync = (certificateID, isValid) => async () => {
    await axios.put(
        buildApiV3Url(`certificates/${certificateID}/update_validation`),
        { paused: isValid },
        { withCredentials: true }
    );
};

export const splitCertificateAsync = (certificateID, postData) => async () => {
    const response = await axios.post(
        buildApiV3Url(`certificates/${certificateID}/split-certificate`),
        postData,
        { withCredentials: true }
    );
    return response.status === 200;
};

export const deleteCertficateAsync = async certificateID => {
    if (Number.isNaN(certificateID) || certificateID === 0) {
        return false;
    }
    const response = await axios.delete(buildApiV3Url(`certificates/${certificateID}`), {
        withCredentials: true,
    });
    return response.status === 200;
};

export const postBulkPrintCertificateApi =
    (
        selectedColumns,
        title,
        showToast,
        hideToast,
        pageSize = 20,
        pageNumber = 0,
        isPending = false
    ) =>
    async (dispatch, getState) => {
        const { isLoadingCertificateExport } = getState().certificate;
        if (isLoadingCertificateExport) {
            return;
        }
        dispatch(setIsLoadingCertificateExport(true));

        // Conditionally use the appropriate filter data function
        const certificateFilterJson = isPending
            ? certificatePendingFiltersData(selectedColumns, getState, title, pageSize, pageNumber)
            : certificateFiltersData(selectedColumns, getState, pageSize, pageNumber);

        // Conditionally build the URL with isPendingCert parameter
        const url = isPending
            ? buildApiV3Url(`certificates/bulk-certificate-print?title=${title}&isPendingCert=true`)
            : buildApiV3Url(`certificates/bulk-certificate-print?title=${title}`);

        const response = await axios.post(url, certificateFilterJson, { withCredentials: true });
        dispatch(setIsBulkDownloadCertificateDialogOpen(false));
        dispatch(setIsLoadingCertificateExport(false));
        if (response.status === 200) {
            const baseURL = `https://${getConfig().hostnames.docLockerClient}`;
            window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
            showToast(
                "success",
                "We are working on your export certificate request. When it's ready, find it in",
                "export-success",
                true,
                "your downloads",
                () => {
                    hideToast("export-success");
                    window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
                },
                "The time it takes to export data depends on the size of the file and network traffic."
            );
            return;
        }
        showToast("error", `Something went wrong`);
    };

/**
 * Wrapper function for backward compatibility.
 * Calls postBulkPrintCertificateApi with isPending=true
 */
export const postBulkPrintPendingCertificateApi =
    (selectedColumns, title, showToast, hideToast, pageSize = 20, pageNumber = 0) =>
    async dispatch => {
        return dispatch(
            postBulkPrintCertificateApi(
                selectedColumns,
                title,
                showToast,
                hideToast,
                pageSize,
                pageNumber,
                true
            )
        );
    };

export const getCustomFieldsValidationResults = certificateID => (dispatch, getState) => {
    const {
        certificateAddedCustomFieldValues,
        certificateExistingCustomFieldValues,
        certificateCustomFields,
        customFields,
    } = getState().certificate;
    const payloadWithCustomFields = {};
    const customFieldIterator = certificateID ? certificateCustomFields : customFields;

    customFieldIterator.forEach(customField => {
        const customFieldId = customField.exists ? customField.fieldId : customField.id;

        if (customField.requiredInDataEntry) {
            let customFieldValue = "";
            if (certificateAddedCustomFieldValues[customFieldId]) {
                customFieldValue = certificateAddedCustomFieldValues[customFieldId];
            } else if (certificateExistingCustomFieldValues[customFieldId]) {
                customFieldValue = certificateExistingCustomFieldValues[customFieldId];
            }
            payloadWithCustomFields[customField.fieldName] = {
                value: customFieldValue,
                validations: [fieldValidationTypeRequired],
            };
        }
    });

    return fieldsValidation(payloadWithCustomFields);
};

export const getCustomFields =
    (reviewPage = false, isOutgoing = false) =>
    async (dispatch, getState) => {
        const { customFields } = getState().certificate;

        // Return cached data if already loaded (for non-reviewPage, non-outgoing context)
        if (!reviewPage && !isOutgoing && customFields && customFields.length > 0) {
            return customFields;
        }

        // Always filter by documentType for incoming/outgoing context
        let filterString = `documentType.Name eq '${documentTypeNames.salesAndUseTax}' and documentType.Outgoing eq ${isOutgoing}`;
        if (reviewPage) {
            filterString += ` and UsesDataEntry eq true`;
        }

        // Use deduplication to prevent concurrent duplicate calls
        certificateCustomFieldsPromise = deduplicateRequest(
            certificateCustomFieldsPromise,
            async () => {
                dispatch(setLoadingCustomFields(true));

                const response = await axios.get(
                    buildApiV3Url(`certificate-custom-fields?$filter=${filterString}&$top=100`),
                    {
                        withCredentials: true,
                    }
                );

                const data = response?.data?.value || [];
                dispatch(setCustomFields(data));
                return data;
            }
        );

        try {
            return await certificateCustomFieldsPromise;
        } finally {
            certificateCustomFieldsPromise = null;
            dispatch(setLoadingCustomFields(false));
        }
    };

export const getCustomFieldFromSlice = () => (dispatch, getState) => {
    const { certificateAddedCustomFieldValues, certificateExistingCustomFieldValues } =
        getState().certificate;
    return { certificateAddedCustomFieldValues, certificateExistingCustomFieldValues };
};

export const getCertificateCustomFields =
    (certificateID, isOutgoing = false, reviewPage = false) =>
    async (dispatch, getState) => {
        const { isLoadingCertificateCustomFields } = getState();
        if (isLoadingCertificateCustomFields) {
            return false;
        }

        let filterString = `documentType.Name eq '${documentTypeNames.salesAndUseTax}' and documentType.Outgoing eq ${isOutgoing}`;
        if (reviewPage) {
            filterString += ` and UsesDataEntry eq true`;
        }

        dispatch(setLoadingCertificateCustomFields(true));
        const allFieldsResponse = await axios.get(
            buildApiV3Url(`certificate-custom-fields?$filter=${filterString}&$top=100`),
            { withCredentials: true }
        );

        let customFields = allFieldsResponse?.data?.value || [];

        const response = await axios.get(
            buildApiV3Url(`certificates/${certificateID}/custom-fields?$top=100`),
            { withCredentials: true }
        );

        const responseFields = response?.data?.value || [];
        const manipulatedCustomFields = {};

        for (let i = 0; i < customFields.length; i++) {
            const field = customFields[i];
            const match = responseFields.find(v => v.fieldId === field.id);

            if (match) {
                field.value = match.value;
                field.id = match.id;
                field.fieldId = match.fieldId;
                field.exists = true;

                manipulatedCustomFields[match.fieldId] =
                    field.type === "multi-select" ? match.value.split("|") : match.value;
            }
        }

        if (Object.keys(manipulatedCustomFields).length > 0) {
            dispatch(setCertificateExistingCustomFieldValues(manipulatedCustomFields));
        }
        dispatch(setCertificateCustomFields(customFields));
        dispatch(setLoadingCertificateCustomFields(false));
        return true;
    };

/**
 * Fetches all certificate-level labels from certificate-labels endpoint.
 * Used for certificate search filters (SelectCertificateLabels, CertificateFilter, etc.).
 */
export const fetchAllCertificateLabels = () => async (dispatch, getState) => {
    const { certificate } = getState();
    const { certificateLabels } = certificate;

    // Skip if labels are already loaded
    if (certificateLabels && certificateLabels.length > 0) {
        return;
    }

    // Use deduplication to prevent concurrent duplicate calls
    certificateLabelsPromise = deduplicateRequest(certificateLabelsPromise, async () => {
        const allLabelsResponse = await axios.get(
            buildApiV3Url(`certificate-labels?$orderBy=name ASC&documentType=1&$top=100`),
            { withCredentials: true }
        );
        if (allLabelsResponse?.data?.value) {
            const transformedCertificateLabels = convertFieldToString(
                allLabelsResponse.data.value,
                "id"
            );
            dispatch(setCertificateLabels(transformedCertificateLabels));
        }
        return allLabelsResponse;
    });

    try {
        return await certificateLabelsPromise;
    } finally {
        certificateLabelsPromise = null;
    }
};

/**
 * Fetches all certificate attachment (document) labels from certificate-attachment-labels endpoint.
 * Used for document label dropdowns (SupportingDocumentsPreviewDialog, EditSupportingDocumentDialog, FileUploaderDialog).
 */
export const fetchAllCertificateAttachmentLabels = () => async (dispatch, getState) => {
    const { certificate, session } = getState();
    const { certificateAttachmentLabels } = certificate;
    const companyId = session?.activeCompany?.id;

    // Skip if labels are already loaded
    if (certificateAttachmentLabels && certificateAttachmentLabels.length > 0) {
        return;
    }

    // Skip if no company context (certificate-attachment-labels is company-scoped)
    if (!companyId) {
        return;
    }

    // Use deduplication to prevent concurrent duplicate calls
    certificateAttachmentLabelsPromise = deduplicateRequest(
        certificateAttachmentLabelsPromise,
        async () => {
            const allLabelsResponse = await axios.get(
                buildApiV3UrlWithQuery(
                    `companies/${companyId}/certificate-attachment-labels`,
                    "",
                    null,
                    100,
                    0,
                    "name ASC"
                ),
                { withCredentials: true }
            );
            const labelsData = allLabelsResponse?.data?.value ?? allLabelsResponse?.data;
            if (labelsData && Array.isArray(labelsData)) {
                const transformedLabels = convertFieldToString(labelsData, "id");
                dispatch(setCertificateAttachmentLabels(transformedLabels));
            }
            return allLabelsResponse;
        }
    );

    try {
        return await certificateAttachmentLabelsPromise;
    } finally {
        certificateAttachmentLabelsPromise = null;
    }
};

/**
 * Fetches certificate attachment labels for document label dropdowns.
 * Dispatches fetchAllCertificateAttachmentLabels (attachment labels are company-scoped, no cert-specific merge).
 */
export const getCertificateAttachmentLabels = () => async (dispatch, getState) => {
    const { isLoadingCertificateAttachmentLabels } = getState().certificate;
    if (isLoadingCertificateAttachmentLabels) {
        return false;
    }

    dispatch(setLoadingCertificateAttachmentLabels(true));
    await dispatch(fetchAllCertificateAttachmentLabels());
    dispatch(setLoadingCertificateAttachmentLabels(false));
    return true;
};

export const getCertificateLabels = certificateId => async (dispatch, getState) => {
    const { isLoadingCertificateLabels } = getState().certificate;
    if (isLoadingCertificateLabels) {
        return false;
    }

    dispatch(setLoadingCertificateLabels(true));
    // Await fetchAllCertificateLabels to ensure deduplication works properly
    await dispatch(fetchAllCertificateLabels());
    if (certificateId) {
        const response = await axios.get(
            buildApiV3Url(`certificates/${certificateId}/certificate-labels`),
            { withCredentials: true }
        );
        if (response?.data?.value) {
            const transformedCertificateLabels = convertFieldToString(response?.data?.value, "id");
            dispatch(setCustomLabels(transformedCertificateLabels));
        }
    }
    dispatch(setLoadingCertificateLabels(false));
    return true;
};

export const getCertificateReviewers = () => async (dispatch, getState) => {
    const { isLoadingCertificateReviewers } = getState().certificate;

    if (isLoadingCertificateReviewers) {
        return false;
    }
    dispatch(setLoadingCertificateReviewers(true));
    const response = await axios.get(buildApiV3Url(`certificates/reviewers`), {
        withCredentials: true,
    });

    if (response?.data) {
        dispatch(setCertificateReviewers(response.data));
    }

    dispatch(setLoadingCertificateReviewers(false));
    return true;
};

export const getCertificateSources = () => async (dispatch, getState) => {
    const { isLoadingCertificateSources } = getState().certificate;

    if (isLoadingCertificateSources) {
        return false;
    }
    dispatch(setLoadingCertificateSources(true));

    const response = await axios.get(buildApiV3Url(`certificate-labels/sources`), {
        withCredentials: true,
    });

    if (response?.data) {
        const transformedCertificateSources = convertFieldToString(response?.data, "id");
        dispatch(setCertificateSources(transformedCertificateSources));
    }

    dispatch(setLoadingCertificateSources(false));
    return true;
};

export const deleteCertificatesAsync =
    (certificateIDs, deleteMultiple, showToast, afterDeleteCallback, useFilterCriteriaForDelete) =>
    async (dispatch, getState) => {
        dispatch(setDeletingCert(true));
        if (!Array.isArray(certificateIDs)) {
            certificateIDs = [certificateIDs];
        }
        certificateIDs = [...new Set(certificateIDs)];

        const certificateFilterJson = useFilterCriteriaForDelete
            ? certificateFiltersData(null, getState, 20, 0)
            : {};
        const { page } = getState().certificate;
        if (certificateIDs?.length > 0 || useFilterCriteriaForDelete) {
            const response = await axios.post(
                buildApiV3Url(`certificates/deletes`),
                {
                    certificateIds: certificateIDs,
                    certificateFilterModel: {
                        ...certificateFilterJson,
                        searchedTotalRecords: page.paginate.totalRecords,
                    },
                },
                { withCredentials: true }
            );
            const toastString = deleteMultiple
                ? "Your bulk certificates delete request has been queued for processing."
                : "Your certificate delete request has been queued for processing.";
            if (response.status === 200) {
                if (Object.keys(response.data).includes("-1")) {
                    showToast("error", response.data["-1"]);
                } else {
                    showToast("success", toastString);
                }
                refreshGrid(getState, dispatch);
            }
        }
        dispatch(setDeletingCert(false));
        if (afterDeleteCallback) afterDeleteCallback();
    };

export const resyncCertificatesAsync = showToast => async (dispatch, getState) => {
    dispatch(setLoading(true));
    const certificateFilterJson = certificateFiltersData(null, getState, 20, 0);
    const { page } = getState().certificate;
    const response = await axios.post(
        buildApiV3Url(`certificates/bulk-api-resend-certificates`),
        {
            certificateIds: [],
            certificateFilterModel: {
                ...certificateFilterJson,
                searchedTotalRecords: page.paginate.totalRecords,
            },
        },
        { withCredentials: true }
    );
    if (response.status === 200) {
        if (Object.hasOwn(response.data, "-1")) {
            showToast("error", response.data["-1"]);
        } else {
            showToast("success", resyncApiCallToastMessage);
        }
    }
    dispatch(setLoading(false));
};

// Revalidate a completed certificate to create a new item for re-review
export const revalidateCertificateAsync = certificateId => async () => {
    try {
        const response = await axios.post(
            buildApiV3Url(`certificates/${certificateId}/revalidate`),
            {},
            { withCredentials: true }
        );

        const newCertificateId = response?.data?.returnValue ?? null;

        const apiSuccess = Boolean(response?.data?.success);

        const message =
            response?.data?.message ??
            (newCertificateId
                ? `Revalidation created (new id ${newCertificateId}).`
                : "Revalidation failed.");
        return { success: apiSuccess, newCertificateId, message };
    } catch {
        return { success: false };
    }
};

export const fetchSmartSearchFilters = (showToast, localInput) => async (dispatch, getState) => {
    const { formState } = getState().certificate;

    if (!localInput) {
        showToast("error", "Please enter a valid query to search in the search bar.");
        return;
    }

    dispatch(
        setFormState({
            ...formState,
            smartSearchError: "",
        })
    );
    dispatch(setSmartSearchFiltersLoading(true));
    dispatch(setLoading(true));
    let response;

    try {
        response = await axios.post(
            buildApiV3Url("nls/certificate-search"),
            {
                text: localInput,
            },
            {
                withCredentials: true,
            }
        );
        dispatch(setSmartSearchFiltersLoading(false));
        const results = response?.data?.results;

        if (!isEmpty(results)) {
            dispatch(setSmartSearchFilters({ ...response?.data?.results }));
        } else {
            dispatch(
                setFormState({
                    ...formState,
                    smartSearchError: "Please enter a valid query to search in the search bar.",
                })
            );
            dispatch(setLoading(false));
        }
    } catch (error) {
        if (error?.response?.status === 422) {
            showToast(
                "error",
                "The request took too long to complete. Please check your network and try again."
            );
        } else {
            showToast(
                "error",
                "Natural language search is temporarily unavailable. Please try again or use manual filters."
            );
        }
        dispatch(setLoading(false));
    }
    return true;
};
export const updateReviewPreferences = () => async (_, getState) => {
    const { isReviewNextDialogOpen, isReviewNextEnabled } = getState().certificate;

    const preferenceValue = JSON.stringify([
        {
            id: "reviewNextCertificate",
            value: isReviewNextEnabled,
        },
        {
            id: "reviewNextCertificateDialog",
            value: isReviewNextDialogOpen,
        },
    ]);
    const payload = {
        preference: "review-certificate",
        preferenceValue,
    };

    return axios.post(buildApiV3Url("ecm-preferences"), payload, {
        withCredentials: true,
        headers: {
            "Content-Type": "application/json",
        },
    });
};

export default certificateSlice.reducer;

export {
    certificateFiltersData,
    fetchCertificatesSearchAPI,
    fetchTaxContentFormsAsync,
    fetchVendorCertificatesSearchAPI,
    getCertificateColumnList,
    getPendingCertificateColumnList,
};
